import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import {
  PencilIcon,
  XMarkIcon,
  ArrowPathIcon,
  DocumentTextIcon,
  PrinterIcon,
  PlusIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  EyeSlashIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { ServerOrder, StaffRole } from '../types';
import { apiService } from '../services/api';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import { useDesktopSidebarCollapsed } from '../context/LayoutContext';
import {
  buildRestaurantFrontendOrigin,
  getShopIdFromAuthToken,
  getShopUsernameFromAuthToken,
} from '../utils/restaurantUtils';
import { getStaffRoleFromToken } from '../utils/staffRoleUtils';
import { maskMobileNumber } from '../utils/phoneMaskUtils';

type PaymentOption = 'cash' | 'card' | 'upi' | 'online' | 'credit_due';

const PAYMENT_OPTIONS: readonly PaymentOption[] = ['cash', 'card', 'upi', 'online', 'credit_due'];

const MARK_PAID_PAYMENT_METHOD_STORAGE_KEY = 'markPaidModalPaymentMethod';

const isPaymentOption = (value: string): value is PaymentOption =>
  (PAYMENT_OPTIONS as readonly string[]).includes(value);

const readStoredMarkPaidPaymentMethod = (): PaymentOption => {
  try {
    const raw = localStorage.getItem(MARK_PAID_PAYMENT_METHOD_STORAGE_KEY);
    if (raw && isPaymentOption(raw)) return raw;
  } catch {
    /* ignore */
  }
  return 'cash';
};

const persistMarkPaidPaymentMethod = (method: PaymentOption) => {
  try {
    localStorage.setItem(MARK_PAID_PAYMENT_METHOD_STORAGE_KEY, method);
  } catch {
    /* ignore */
  }
};

type NewOrderChannel = 'table' | 'delivery' | 'pickup' | 'swiggy' | 'zomato';

const NEW_ORDER_CHANNEL_STORAGE_KEY = 'newOrderModalChannel';
const NEW_ORDER_TABLE_STORAGE_KEY = 'newOrderModalTableId';
const BILL_PRINT_FORMAT_STORAGE_KEY = 'menunix_bill_print_format';

type BillPrintFormat = 'thermal80' | 'thermal58' | 'a4' | 'a5' | 'letter';

const BILL_PRINT_FORMAT_OPTIONS: { value: BillPrintFormat; label: string }[] = [
  { value: 'thermal80', label: 'Thermal — 80mm (POS)' },
  { value: 'thermal58', label: 'Thermal — 58mm (compact)' },
  { value: 'a4', label: 'Standard — A4' },
  { value: 'a5', label: 'Standard — A5' },
  { value: 'letter', label: 'Standard — Letter (US)' },
];

const isBillPrintFormat = (value: string): value is BillPrintFormat =>
  BILL_PRINT_FORMAT_OPTIONS.some((option) => option.value === value);

const readStoredBillPrintFormat = (): BillPrintFormat => {
  try {
    const raw = localStorage.getItem(BILL_PRINT_FORMAT_STORAGE_KEY);
    if (raw === 'thermal') return 'thermal80';
    if (raw === 'regular') return 'a4';
    if (raw && isBillPrintFormat(raw)) return raw;
  } catch {
    /* ignore */
  }
  return 'thermal80';
};

const persistBillPrintFormat = (format: BillPrintFormat) => {
  try {
    localStorage.setItem(BILL_PRINT_FORMAT_STORAGE_KEY, format);
  } catch {
    /* ignore */
  }
};

type BillPrintLayout = {
  compact: boolean;
  wrapMaxWidth: string;
  bodyPadding: string;
  bodyFontSize: string;
  titleFontSize: string;
  totalFontSize: string;
  pageMargin: string;
  pageSize: string;
};

const getBillPrintLayout = (format: BillPrintFormat): BillPrintLayout => {
  switch (format) {
    case 'thermal58':
      return {
        compact: true,
        wrapMaxWidth: '200px',
        bodyPadding: '6px',
        bodyFontSize: '10px',
        titleFontSize: '12px',
        totalFontSize: '12px',
        pageMargin: '4mm',
        pageSize: '58mm auto',
      };
    case 'thermal80':
      return {
        compact: true,
        wrapMaxWidth: '280px',
        bodyPadding: '8px',
        bodyFontSize: '11px',
        titleFontSize: '14px',
        totalFontSize: '13px',
        pageMargin: '6mm',
        pageSize: '80mm auto',
      };
    case 'a5':
      return {
        compact: false,
        wrapMaxWidth: '520px',
        bodyPadding: '14px',
        bodyFontSize: '12px',
        titleFontSize: '18px',
        totalFontSize: '14px',
        pageMargin: '8mm',
        pageSize: 'A5',
      };
    case 'letter':
      return {
        compact: false,
        wrapMaxWidth: '720px',
        bodyPadding: '18px',
        bodyFontSize: '13px',
        titleFontSize: '20px',
        totalFontSize: '15px',
        pageMargin: '10mm',
        pageSize: 'letter',
      };
    case 'a4':
    default:
      return {
        compact: false,
        wrapMaxWidth: '720px',
        bodyPadding: '18px',
        bodyFontSize: '13px',
        titleFontSize: '20px',
        totalFontSize: '15px',
        pageMargin: '10mm',
        pageSize: 'A4',
      };
  }
};

/** Delivery staff orders list: backend expects multiple `status` query params (Ready + Picked Up). */
const DELIVERY_ORDER_LIST_STATUSES: string[] = ['Ready', 'Picked Up'];

const ALL_ORDER_STATUSES = ['New', 'Ready', 'Picked Up', 'Delivered', 'Cancelled'] as const;

const TERMINAL_ACTION_LONG_PRESS_MS = 5000;

const isTerminalOrderStatus = (order: ServerOrder): boolean => {
  const status = String(order.status ?? '').trim();
  return status === 'Delivered' || status === 'Cancelled';
};

/** Stable allowed-status options per role (avoid recreating arrays every render). */
const getAllowedOrderStatusFilters = (staffRole: StaffRole | null, canSeeAllStatuses: boolean): string[] => {
  if (staffRole === 'serviceStaff') return ['Ready'];
  if (staffRole === 'deliveryStaff') return ['Ready', 'Picked Up'];
  if (staffRole === 'kitchenStaff') return ['New'];
  if (canSeeAllStatuses) return [...ALL_ORDER_STATUSES];
  return ['Ready'];
};

/** Catalog/product change signal — when it changes, refresh orders (same host as product listing). */
const PRODUCT_TABLE_TIMESTAMP_URL = import.meta.env.VITE_PRODUCT_TABLE_TIMESTAMP_URL;

const isNewOrderChannel = (v: string): v is NewOrderChannel =>
  ['table', 'delivery', 'pickup', 'swiggy', 'zomato'].includes(v);

const readStoredNewOrderChannel = (): NewOrderChannel => {
  try {
    const raw = localStorage.getItem(NEW_ORDER_CHANNEL_STORAGE_KEY);
    if (raw && isNewOrderChannel(raw)) {
      return raw;
    }
  } catch {
    /* ignore */
  }
  return 'pickup';
};

const readStoredNewOrderTableId = (): string => {
  try {
    return localStorage.getItem(NEW_ORDER_TABLE_STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const NEW_ORDER_TYPE_OPTIONS: { value: NewOrderChannel; label: string }[] = [
  { value: 'pickup', label: 'Pickup' },
  { value: 'table', label: 'Table' },
  { value: 'delivery', label: 'Delivery' },
  { value: 'swiggy', label: 'Swiggy' },
  { value: 'zomato', label: 'Zomato' },
];

const getTodayDateString = (): string => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/** Matches roles that see order date filters (`canUseOrderFilters`): today default only for those. */
const getInitialOrderDateFilterValue = (): string => {
  const staffRole = getStaffRoleFromToken();
  if (['kitchenStaff', 'deliveryStaff', 'serviceStaff'].includes(staffRole || '')) {
    return '';
  }
  return getTodayDateString();
};

/** Merge key for list rows: referenceId is unique for orders; fall back for older payloads. */
const getOrderListMergeKey = (order: ServerOrder): string => {
  const ref = String(order.referenceId ?? '').trim();
  if (ref) return ref;
  const inv = String(order.invoiceNo ?? '').trim();
  if (inv) return inv;
  return String(order._id ?? '').trim();
};

/**
 * After a mutation, API returns one page slice. Patch matching rows in the full list by merge key
 * so mobile infinite-scroll lists are not replaced by a single page.
 */
const mergeOrderPageIntoList = (existing: ServerOrder[], sliceFromApi: ServerOrder[]): ServerOrder[] => {
  if (!sliceFromApi.length) return existing.length ? [...existing] : existing;
  if (!existing.length) return sliceFromApi.map((row) => ({ ...row }));

  const incomingByKey = new Map<string, ServerOrder>();
  const incomingById = new Map<string, ServerOrder>();
  for (const row of sliceFromApi) {
    const k = getOrderListMergeKey(row);
    if (k) incomingByKey.set(k, row);
    const id = String(row._id ?? '').trim();
    if (id) incomingById.set(id, row);
  }
  if (incomingByKey.size === 0 && incomingById.size === 0) {
    return [...existing];
  }

  const mergedKeys = new Set<string>();
  let changed = false;
  const merged = existing.map((row) => {
    const k = getOrderListMergeKey(row);
    const id = String(row._id ?? '').trim();
    const replacement =
      (k ? incomingByKey.get(k) : undefined) ?? (id ? incomingById.get(id) : undefined);
    if (replacement) {
      if (k) mergedKeys.add(k);
      if (id) mergedKeys.add(id);
      changed = true;
      return { ...replacement };
    }
    return row;
  });

  for (const row of sliceFromApi) {
    const k = getOrderListMergeKey(row);
    const id = String(row._id ?? '').trim();
    const seen = (k && mergedKeys.has(k)) || (id && mergedKeys.has(id));
    if (!seen) {
      merged.push({ ...row });
      if (k) mergedKeys.add(k);
      if (id) mergedKeys.add(id);
      changed = true;
    }
  }

  return changed ? merged : [...merged];
};

const ORDER_LIST_PAGE_SIZE_KEY = 'orderListPageSize';
const ORDER_LIST_PAGE_SIZE_MANUAL_KEY = 'orderListPageSizeManual';
const ORDER_PAGE_SIZE_PRESETS = [5, 10, 20] as const;
const ORDER_PAGE_SIZE_MIN = 5;
const ORDER_PAGE_SIZE_MAX = 30;

const getPageSizeSelectOptions = (currentLimit: number): number[] => {
  const values = new Set<number>([...ORDER_PAGE_SIZE_PRESETS, currentLimit]);
  return [...values].sort((a, b) => a - b);
};

const readManualPageSize = (): number | null => {
  try {
    if (localStorage.getItem(ORDER_LIST_PAGE_SIZE_MANUAL_KEY) !== '1') return null;
    const saved = Number(localStorage.getItem(ORDER_LIST_PAGE_SIZE_KEY));
    if (Number.isFinite(saved) && saved >= ORDER_PAGE_SIZE_MIN && saved <= ORDER_PAGE_SIZE_MAX) {
      return saved;
    }
  } catch {
    /* ignore */
  }
  return null;
};

const isPageSizeManual = (): boolean => {
  try {
    return localStorage.getItem(ORDER_LIST_PAGE_SIZE_MANUAL_KEY) === '1';
  } catch {
    return false;
  }
};

/** Rows that fit in the visible list area for the current viewport / layout. */
const computeFittingOrderPageSize = (options: {
  hasFiltersBar: boolean;
  mobileFiltersOpen?: boolean;
}): number => {
  if (typeof window === 'undefined') return 10;

  const isMobile = window.matchMedia('(max-width: 767px)').matches;
  const rowPx = isMobile ? 300 : 92;

  const headerPx = 64;
  const filtersPx = options.hasFiltersBar
    ? isMobile
      ? options.mobileFiltersOpen
        ? 280
        : 56
      : 176
    : isMobile
      ? 16
      : 32;
  const mainPaddingPx = isMobile ? 32 : 48;
  const paginationPx = isMobile ? 48 : 80;
  const tableHeadPx = isMobile ? 0 : 48;
  const gapsPx = isMobile ? 40 : 28;
  const bottomNavPx = isMobile ? 72 : 0;

  const available =
    window.innerHeight -
    headerPx -
    filtersPx -
    mainPaddingPx -
    paginationPx -
    tableHeadPx -
    gapsPx -
    bottomNavPx;

  const fitting = Math.floor(available / rowPx);
  return Math.min(ORDER_PAGE_SIZE_MAX, Math.max(ORDER_PAGE_SIZE_MIN, fitting));
};

const getOrderUpdatedBy = (order: ServerOrder, keys: string[]): string | null => {
  const o = order as unknown as Record<string, unknown>;
  for (const key of keys) {
    const raw = o[key];
    if (raw == null) continue;
    const value = String(raw).trim();
    if (value) return value;
  }
  return null;
};

const getOrderStatusUpdatedBy = (order: ServerOrder): string | null =>
  getOrderUpdatedBy(order, ['statusUpdated', 'status_updated']);

const getOrderPaymentUpdatedBy = (order: ServerOrder): string | null =>
  getOrderUpdatedBy(order, ['paymentUpdated', 'payment_updated']);

/** Tap-friendly tooltip (works on mobile); shows who updated status/payment. */
const OrderUpdatedByTooltip: React.FC<{
  heading: string;
  updatedBy: string;
  align?: 'start' | 'end';
}> = ({ heading, updatedBy, align = 'start' }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (event: Event) => {
      const target = event.target as Node | null;
      if (rootRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [open]);

  const popoverPosition = align === 'end' ? 'right-0' : 'left-0';

  return (
    <div ref={rootRef} className="relative inline-flex shrink-0 align-middle">
      <button
        type="button"
        className="inline-flex h-5 w-5 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500/40"
        aria-label={`${heading}: ${updatedBy}`}
        aria-expanded={open}
        title={updatedBy}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((prev) => !prev);
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <InformationCircleIcon className="h-3.5 w-3.5" aria-hidden />
      </button>
      {open && (
        <div
          role="tooltip"
          className={`absolute top-full z-50 mt-1 min-w-[10rem] max-w-[16rem] rounded-md border border-gray-200 bg-white px-2.5 py-2 text-left shadow-lg ${popoverPosition}`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">{heading}</p>
          <p className="mt-0.5 break-words text-xs leading-snug text-gray-800">{updatedBy}</p>
        </div>
      )}
    </div>
  );
};

const getOrderPaymentNote = (order: ServerOrder): string | null => {
  const fromTyped = String(order.paymentNote ?? '').trim();
  if (fromTyped) return fromTyped;
  const o = order as unknown as Record<string, unknown>;
  const legacy = o.payment_note;
  if (typeof legacy === 'string' && legacy.trim()) return legacy.trim();
  return null;
};

const getOrderCustomerMobile = (order: ServerOrder): string | null => {
  const raw = order.createdBy ?? order.user;
  if (raw == null || raw === '') return null;
  const value = String(raw).trim();
  return value || null;
};

const getOrderTypeFromOrder = (order: ServerOrder): string =>
  String(order.orderType ?? (order as { order_type?: unknown }).order_type ?? '').trim();

const formatOrderTypeLabel = (raw: string): string => {
  const value = raw.trim();
  if (!value) return '';
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const getOrderTypeLabel = (order: ServerOrder): string =>
  formatOrderTypeLabel(getOrderTypeFromOrder(order));

const isDeliveryOrderType = (order: ServerOrder): boolean =>
  getOrderTypeFromOrder(order).toLowerCase() === 'delivery';

const formatOrderAddressValue = (value: unknown): string | null => {
  if (value == null) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    const o = value as Record<string, unknown>;
    const parts = [o.street, o.line1, o.line2, o.area, o.city, o.state, o.pincode, o.zip, o.postalCode]
      .map((part) => (part == null ? '' : String(part).trim()))
      .filter(Boolean);
    return parts.length ? parts.join(', ') : null;
  }
  return null;
};

const getOrderDeliveryAddress = (order: ServerOrder): string | null => {
  if (!isDeliveryOrderType(order)) return null;

  const fromTyped =
    formatOrderAddressValue(order.address) ?? formatOrderAddressValue(order.deliveryAddress);
  if (fromTyped) return fromTyped;

  const o = order as unknown as Record<string, unknown>;
  const extraKeys = ['delivery_address', 'customerAddress', 'customer_address', 'shippingAddress'];
  for (const key of extraKeys) {
    const formatted = formatOrderAddressValue(o[key]);
    if (formatted) return formatted;
  }
  return null;
};

const hasOrderCustomerColumnContent = (order: ServerOrder): boolean =>
  Boolean(getOrderCustomerMobile(order) || getOrderDeliveryAddress(order));

interface OrderCustomerMobileProps {
  value?: string | null;
  deliveryAddress?: string | null;
  canOpenCustomerBills: boolean;
  onOpenCustomerBills?: (customerId: string) => void;
  variant?: 'table' | 'inline';
}

const CustomerVisibilityToggle: React.FC<{
  revealed: boolean;
  hasDeliveryAddress: boolean;
  onToggle: (e: React.MouseEvent) => void;
}> = ({ revealed, hasDeliveryAddress, onToggle }) => (
  <button
    type="button"
    onClick={onToggle}
    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-50 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-500/30"
    title={
      revealed
        ? hasDeliveryAddress
          ? 'Hide customer details'
          : 'Hide number'
        : hasDeliveryAddress
          ? 'Show customer details'
          : 'Show full number'
    }
    aria-label={
      revealed
        ? hasDeliveryAddress
          ? 'Hide customer details'
          : 'Hide number'
        : hasDeliveryAddress
          ? 'Show customer details'
          : 'Show full number'
    }
    aria-pressed={revealed}
  >
    {revealed ? (
      <EyeSlashIcon className="h-4 w-4" aria-hidden />
    ) : (
      <EyeIcon className="h-4 w-4" aria-hidden />
    )}
  </button>
);

const OrderCustomerMobile: React.FC<OrderCustomerMobileProps> = ({
  value,
  deliveryAddress,
  canOpenCustomerBills,
  onOpenCustomerBills,
  variant = 'table',
}) => {
  const [revealed, setRevealed] = React.useState(false);
  const mobile = value?.trim() || '';
  const address = deliveryAddress?.trim() || '';
  const hasMobile = Boolean(mobile);
  const hasAddress = Boolean(address);
  const masked = hasMobile ? maskMobileNumber(mobile) : '';

  const handleToggleVisibility = (e: React.MouseEvent) => {
    e.stopPropagation();
    setRevealed((prev) => !prev);
  };

  const handleOpenBills = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (canOpenCustomerBills && onOpenCustomerBills && hasMobile) {
      onOpenCustomerBills(mobile);
    }
  };

  const numberClass =
    variant === 'table'
      ? 'font-mono text-sm tabular-nums text-gray-900'
      : 'font-mono tabular-nums text-gray-900';

  const addressClass =
    variant === 'table'
      ? 'mt-1 max-w-[14rem] break-words text-xs leading-snug text-gray-600'
      : 'break-words text-xs leading-snug text-gray-600';

  const hiddenPlaceholder =
    !revealed && !hasMobile && hasAddress ? (
      <span
        className={
          variant === 'inline'
            ? 'block w-full text-sm text-gray-400'
            : 'text-sm text-gray-400'
        }
      >
        ••••••
      </span>
    ) : null;

  const inlineNumberClass = 'block w-full break-all text-left font-mono tabular-nums text-sm text-gray-900';

  const numberNode = hasMobile ? (
    revealed && canOpenCustomerBills && onOpenCustomerBills ? (
      <button
        type="button"
        onClick={handleOpenBills}
        className={`${variant === 'inline' ? inlineNumberClass : numberClass} font-medium text-green-700 hover:text-green-900 hover:underline`}
        title="View unpaid bills"
      >
        {mobile}
      </button>
    ) : (
      <span className={variant === 'inline' ? inlineNumberClass : numberClass}>
        {revealed ? mobile : masked}
      </span>
    )
  ) : null;

  const addressNode =
    revealed && hasAddress ? (
      variant === 'inline' ? (
        <span className="block w-full break-words text-xs leading-snug text-gray-600">{address}</span>
      ) : (
        <p className={addressClass}>{address}</p>
      )
    ) : null;

  const detailsBlock = (
    <div className={`min-w-0 flex-1 ${variant === 'inline' ? 'flex w-full flex-col gap-0.5' : ''}`}>
      {numberNode ?? hiddenPlaceholder}
      {addressNode}
    </div>
  );

  const visibilityToggle = (
    <CustomerVisibilityToggle
      revealed={revealed}
      hasDeliveryAddress={hasAddress}
      onToggle={handleToggleVisibility}
    />
  );

  if (variant === 'table') {
    return (
      <div className="flex items-start gap-2">
        {detailsBlock}
        {visibilityToggle}
      </div>
    );
  }

  const showDetailsBlock = Boolean(numberNode ?? hiddenPlaceholder ?? addressNode);

  return (
    <div className="space-y-1.5 text-sm text-gray-700">
      <div className="flex items-center justify-between gap-2">
        <p className="text-gray-500">Customer:</p>
        {visibilityToggle}
      </div>
      {showDetailsBlock && (
        <div className="w-full min-w-0">{detailsBlock}</div>
      )}
    </div>
  );
};

const OrderList: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const { user } = useAuth();
  const desktopSidebarCollapsed = useDesktopSidebarCollapsed();
  const [orders, setOrders] = useState<ServerOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(() => {
    const manual = readManualPageSize();
    if (manual !== null) return manual;
    if (typeof window === 'undefined') return 10;
    const role = getStaffRoleFromToken();
    const hasFiltersBar = !['kitchenStaff', 'deliveryStaff', 'serviceStaff'].includes(role || '');
    return computeFittingOrderPageSize({ hasFiltersBar, mobileFiltersOpen: false });
  });
  const pageSizeManualRef = useRef(isPageSizeManual());
  const pageSizeOptions = useMemo(() => getPageSizeSelectOptions(limit), [limit]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [selectedOrder, setSelectedOrder] = useState<ServerOrder | null>(null);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [newStatus, setNewStatus] = useState('Confirmed');
  const [statusComment, setStatusComment] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [startDate, setStartDate] = useState(() => getInitialOrderDateFilterValue());
  const [endDate, setEndDate] = useState(() => getInitialOrderDateFilterValue());
  const [statusFilter, setStatusFilter] = useState(() => {
    return localStorage.getItem('orderStatusFilter') || '';
  });
  const [tableFilter, setTableFilter] = useState(() => {
    return localStorage.getItem('orderTableFilter') || '';
  });
  const [tables, setTables] = useState<Array<{_id: string, name: string, status: number}>>([]);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [tablesLoading, setTablesLoading] = useState(true);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [customerOrders, setCustomerOrders] = useState<ServerOrder[]>([]);
  const [customerOrdersLoading, setCustomerOrdersLoading] = useState(false);
  const [isTableModalOpen, setIsTableModalOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState<string>('');
  const [tableDetails, setTableDetails] = useState<any>(null);
  const [tableDetailsLoading, setTableDetailsLoading] = useState(false);
  const [isRemoveSessionModalOpen, setIsRemoveSessionModalOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [isRemovingSession, setIsRemovingSession] = useState(false);
  const [isBillModalOpen, setIsBillModalOpen] = useState(false);
  const [selectedBillOrder, setSelectedBillOrder] = useState<ServerOrder | null>(null);
  const [billPrintFormat, setBillPrintFormat] = useState<BillPrintFormat>(() => readStoredBillPrintFormat());

  const handleBillPrintFormatChange = (format: BillPrintFormat) => {
    setBillPrintFormat(format);
    persistBillPrintFormat(format);
  };
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentMethodSelection, setPaymentMethodSelection] = useState<PaymentOption>(() =>
    readStoredMarkPaidPaymentMethod()
  );
  const [paymentReferenceId, setPaymentReferenceId] = useState('');
  const [cashReceivedAmount, setCashReceivedAmount] = useState('');
  const [paymentNote, setPaymentNote] = useState('');
  const [isSendingPaymentLink, setIsSendingPaymentLink] = useState(false);
  const [isNewOrderModalOpen, setIsNewOrderModalOpen] = useState(false);
  const [newOrderChannel, setNewOrderChannel] = useState<NewOrderChannel>(() => readStoredNewOrderChannel());
  const [newOrderTableId, setNewOrderTableId] = useState(() => readStoredNewOrderTableId());
  const [isNewOrderPreviewModalOpen, setIsNewOrderPreviewModalOpen] = useState(false);
  const [newOrderPreviewUrl, setNewOrderPreviewUrl] = useState('');
  const [isKitchenItemsModalOpen, setIsKitchenItemsModalOpen] = useState(false);
  const [selectedKitchenOrder, setSelectedKitchenOrder] = useState<ServerOrder | null>(null);
  const [unlockedTerminalOrderActions, setUnlockedTerminalOrderActions] = useState<Set<string>>(
    () => new Set()
  );
  const terminalLongPressTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  /** Prevents duplicate mobile "load next page" triggers (IntersectionObserver + stale refs). */
  const mobileInfiniteLockRef = React.useRef(false);
  const mobileSentinelRef = React.useRef<HTMLDivElement | null>(null);
  /** Ignore stale paginated responses when a newer fetch started (status update vs poll race). */
  const ordersFetchGenerationRef = useRef(0);
  const hasLoadedOrdersOnceRef = useRef(false);

  const staffRole = getStaffRoleFromToken();
  const canSeeAllStatuses = ['shopOwner', 'manager', 'orderManager', 'counterStaff'].includes(staffRole || '');
  const canCreateOrder = canSeeAllStatuses;
  const canMarkPayment = staffRole !== 'serviceStaff' && staffRole !== 'kitchenStaff';
  const isKitchenStaff = staffRole === 'kitchenStaff';
  const isServiceStaff = staffRole === 'serviceStaff';
  const canShowCustomerColumn = !isKitchenStaff && !isServiceStaff;
  const canViewBills = !['serviceStaff', 'kitchenStaff', 'deliveryStaff'].includes(staffRole || '');
  const canViewAmount = !['serviceStaff', 'kitchenStaff', 'deliveryStaff'].includes(staffRole || '');
  const canUseOrderFilters = !['kitchenStaff', 'deliveryStaff', 'serviceStaff'].includes(staffRole || '');
  const canUseTableFilter = canUseOrderFilters;
  const canViewCustomerUnpaidBills = canViewBills;

  const applyAutoPageSize = useCallback(() => {
    if (pageSizeManualRef.current) return;
    const next = computeFittingOrderPageSize({
      hasFiltersBar: canUseOrderFilters,
      mobileFiltersOpen: showMobileFilters,
    });
    setLimit((prev) => (prev === next ? prev : next));
  }, [canUseOrderFilters, showMobileFilters]);

  const prevLimitRef = useRef(limit);
  useEffect(() => {
    if (prevLimitRef.current !== limit) {
      setPage(1);
      prevLimitRef.current = limit;
    }
  }, [limit]);

  useLayoutEffect(() => {
    applyAutoPageSize();
  }, [applyAutoPageSize]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const onResize = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(applyAutoPageSize, 200);
    };
    window.addEventListener('resize', onResize);
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      window.removeEventListener('resize', onResize);
    };
  }, [applyAutoPageSize]);

  const handlePageSizeChange = (value: number) => {
    pageSizeManualRef.current = true;
    try {
      localStorage.setItem(ORDER_LIST_PAGE_SIZE_MANUAL_KEY, '1');
      localStorage.setItem(ORDER_LIST_PAGE_SIZE_KEY, String(value));
    } catch {
      /* ignore */
    }
    setPage(1);
    setLimit(value);
  };

  const ordersListStatusParam = useMemo((): string | string[] | undefined => {
    if (staffRole === 'deliveryStaff') return DELIVERY_ORDER_LIST_STATUSES;
    const t = statusFilter.trim();
    return t || undefined;
  }, [staffRole, statusFilter]);

  const allowedStatusFilterValues = useMemo(
    () => getAllowedOrderStatusFilters(staffRole, canSeeAllStatuses),
    [staffRole, canSeeAllStatuses]
  );

  const allowedStatusTargets = useMemo(() => {
    if (staffRole === 'shopOwner' || staffRole === 'manager' || staffRole === 'orderManager') {
      return ['New', 'Ready', 'Picked Up', 'Delivered'];
    }
    if (staffRole === 'kitchenStaff') return ['Ready'];
    if (staffRole === 'serviceStaff') return ['Delivered'];
    if (staffRole === 'counterStaff') return ['Delivered'];
    if (staffRole === 'deliveryStaff') return ['Picked Up', 'Delivered'];
    return [];
  }, [staffRole]);

  const ordersHeadingParts = useMemo(() => {
    const trimmed = statusFilter.trim();
    if (trimmed) return { emphasis: trimmed, rest: 'Orders' as const };
    if (canSeeAllStatuses) return { emphasis: '', rest: 'Orders' as const };
    const fallback = allowedStatusFilterValues[0]?.trim();
    return fallback
      ? { emphasis: fallback, rest: 'Orders' as const }
      : { emphasis: '', rest: 'Orders' as const };
  }, [statusFilter, canSeeAllStatuses, allowedStatusFilterValues]);

  const isOrderPaid = (order: ServerOrder) => {
    const raw = order.paymentStatus;
    if (raw === undefined || raw === null || raw === '') return false;
    if (typeof raw === 'number') return raw === 1;
    const s = String(raw).trim().toLowerCase();
    return s === '1' || s === 'true' || s === 'paid' || s === 'completed';
  };
  const isPaymentPending = (order: ServerOrder) => {
    const raw = order.paymentStatus;
    if (raw === undefined || raw === null || raw === '') return true;
    if (typeof raw === 'number') return raw === 0;
    const s = String(raw).trim().toLowerCase();
    return s === '0' || s === 'false' || s === 'pending' || s === 'unpaid';
  };

  const getOrderTypeValue = (order: ServerOrder): string => {
    const raw = (order as any)?.orderType;
    return String(raw ?? '').trim();
  };

  const isDeliveryOrder = (order: ServerOrder): boolean =>
    getOrderTypeValue(order).toLowerCase() === 'delivery';

  const canUpdateOrderStatus = (order: ServerOrder): boolean => {
    const current = String(order.status ?? '');

    if (staffRole === 'shopOwner' || staffRole === 'manager' || staffRole === 'orderManager') return true;
    if (staffRole === 'serviceStaff') return current === 'Ready';
    if (staffRole === 'deliveryStaff') return current === 'Ready' || current === 'Picked Up';
    if (staffRole === 'kitchenStaff') return current === 'New';
    if (staffRole === 'counterStaff') return current === 'Ready' || current === 'Picked Up';
    return false;
  };

  const canCancelOrder = (order: ServerOrder): boolean => {
    void order;
    return staffRole === 'shopOwner' || staffRole === 'manager' || staffRole === 'orderManager';
  };

  const isTerminalOrderActionsUnlocked = useCallback(
    (order: ServerOrder): boolean => {
      const id = order._id;
      return Boolean(id && unlockedTerminalOrderActions.has(id));
    },
    [unlockedTerminalOrderActions]
  );

  const showUpdateStatusButton = (order: ServerOrder): boolean =>
    canUpdateOrderStatus(order) &&
    (!isTerminalOrderStatus(order) || isTerminalOrderActionsUnlocked(order));

  const showCancelOrderButton = (order: ServerOrder): boolean =>
    canCancelOrder(order) &&
    (!isTerminalOrderStatus(order) || isTerminalOrderActionsUnlocked(order));

  const clearTerminalLongPressTimer = useCallback((orderId: string) => {
    const timer = terminalLongPressTimersRef.current[orderId];
    if (timer) {
      clearTimeout(timer);
      delete terminalLongPressTimersRef.current[orderId];
    }
  }, []);

  const unlockTerminalOrderActions = useCallback((orderId: string) => {
    setUnlockedTerminalOrderActions((prev) => {
      const next = new Set(prev);
      next.add(orderId);
      return next;
    });
  }, []);

  const handleTerminalStatusLongPressStart = useCallback(
    (order: ServerOrder) => {
      const id = order._id;
      if (!id || !isTerminalOrderStatus(order)) return;

      clearTerminalLongPressTimer(id);
      terminalLongPressTimersRef.current[id] = setTimeout(() => {
        delete terminalLongPressTimersRef.current[id];
        unlockTerminalOrderActions(id);
      }, TERMINAL_ACTION_LONG_PRESS_MS);
    },
    [clearTerminalLongPressTimer, unlockTerminalOrderActions]
  );

  const handleTerminalStatusLongPressEnd = useCallback(
    (order: ServerOrder) => {
      const id = order._id;
      if (!id) return;
      clearTerminalLongPressTimer(id);
    },
    [clearTerminalLongPressTimer]
  );

  const getTerminalStatusBadgeInteractionProps = (order: ServerOrder) => {
    if (!isTerminalOrderStatus(order)) return {};
    return {
      onContextMenu: (e: React.MouseEvent) => e.preventDefault(),
      onPointerDown: (e: React.PointerEvent) => {
        e.stopPropagation();
        handleTerminalStatusLongPressStart(order);
      },
      onPointerUp: (e: React.PointerEvent) => {
        e.stopPropagation();
        handleTerminalStatusLongPressEnd(order);
      },
      onPointerLeave: () => handleTerminalStatusLongPressEnd(order),
      onPointerCancel: () => handleTerminalStatusLongPressEnd(order),
    };
  };

  const getOrderStatusBadgeClassName = (status: string): string => {
    if (status === 'New') return 'bg-amber-100 text-amber-800';
    if (status === 'Ready') return 'bg-teal-100 text-teal-900';
    if (status === 'Picked Up') return 'bg-orange-100 text-orange-900';
    if (status === 'Delivered') return 'bg-green-100 text-green-800';
    if (status === 'Cancelled') return 'bg-red-100 text-red-800';
    return 'bg-gray-100 text-gray-800';
  };

  const isStatusUpdateAllowed = (order: ServerOrder, targetStatus: string): boolean => {
    if (!canUpdateOrderStatus(order)) return false;

    const current = String(order.status ?? '');
    // Picked Up → Delivered: require payment for everyone except shop owner / manager (covers
    // delivery mis-tagged as orderManager and other roles that can reach this transition).
    if (
      current === 'Picked Up' &&
      targetStatus === 'Delivered' &&
      staffRole !== 'shopOwner' &&
      staffRole !== 'manager' &&
      !isOrderPaid(order)
    ) {
      return false;
    }

    if (staffRole === 'deliveryStaff') {
      if (current === 'Ready') return targetStatus === 'Picked Up';
      if (current === 'Picked Up') return targetStatus === 'Delivered';
      return false;
    }

    if (
      (staffRole === 'shopOwner' || staffRole === 'manager' || staffRole === 'orderManager') &&
      targetStatus === 'Picked Up' &&
      !isDeliveryOrder(order)
    ) {
      return false;
    }

    return allowedStatusTargets.includes(targetStatus);
  };

  useEffect(() => {
    return () => {
      Object.values(terminalLongPressTimersRef.current).forEach(clearTimeout);
      terminalLongPressTimersRef.current = {};
    };
  }, []);

  // Clamp statusFilter when role/user changes or value is invalid (avoid unstable deps + stale LS after logout/login)
  useEffect(() => {
    if (canSeeAllStatuses) return;

    const allowed = getAllowedOrderStatusFilters(staffRole, canSeeAllStatuses);
    setStatusFilter((prev) => (allowed.includes(prev) ? prev : allowed[0] || ''));
  }, [user?.mobile, staffRole, canSeeAllStatuses, statusFilter]);

  // Ensure page starts at top when visiting this screen
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem(NEW_ORDER_CHANNEL_STORAGE_KEY, newOrderChannel);
    } catch {
      /* ignore */
    }
  }, [newOrderChannel]);

  useEffect(() => {
    try {
      if (newOrderTableId) {
        localStorage.setItem(NEW_ORDER_TABLE_STORAGE_KEY, newOrderTableId);
      } else {
        localStorage.removeItem(NEW_ORDER_TABLE_STORAGE_KEY);
      }
    } catch {
      /* ignore */
    }
  }, [newOrderTableId]);

  // Load tables from localStorage, or fetch from API if not available
  useEffect(() => {
    const loadTables = async () => {
      try {
        // First, try to load from localStorage
        const storedTables = localStorage.getItem('tables');
        if (storedTables) {
          const parsedTables = JSON.parse(storedTables);
          // Handle both direct array and response format { status: true, data: [...] }
          let tablesArray: Array<{_id: string, name: string, status: number}> = [];
          
          if (Array.isArray(parsedTables)) {
            tablesArray = parsedTables;
          } else if (parsedTables.data && Array.isArray(parsedTables.data)) {
            tablesArray = parsedTables.data;
          } else if (parsedTables.status && Array.isArray(parsedTables.data)) {
            tablesArray = parsedTables.data;
          }
          
          if (tablesArray.length > 0) {
            setTables(tablesArray);
            setTablesLoading(false);
            return;
          }
        }
        
        // If not in localStorage or empty, fetch from API
        const response = await apiService.getTables();
        if (response && response.status) {
          const tablesData = response.data || [];
          setTables(tablesData);
          // Store in localStorage for future use
          localStorage.setItem('tables', JSON.stringify(tablesData));
        } else {
          setTables([]);
        }
      } catch (error) {
        console.error('Failed to load tables:', error);
        setTables([]);
      } finally {
        setTablesLoading(false);
      }
    };

    loadTables();
  }, []);

  const refreshOrdersPage = useCallback(
    async (options: { targetPage: number; append?: boolean; merge?: boolean }) => {
      const generation = ++ordersFetchGenerationRef.current;
      const isLoadMore = Boolean(options.append);
      const isFirstPaint = !hasLoadedOrdersOnceRef.current && !isLoadMore;

      if (isLoadMore) {
        setIsLoadingMore(true);
      } else if (isFirstPaint) {
        setLoading(true);
      }

      setError('');
      try {
        const { startParam, endParam } = getDateRange(startDate, endDate);
        const response: any = await apiService.getOrdersPaginated(
          options.targetPage,
          limit,
          startParam,
          endParam,
          ordersListStatusParam,
          tableFilter
        );

        if (generation !== ordersFetchGenerationRef.current) return;

        if (response && response.status) {
          const slice: ServerOrder[] = response.data || [];
          if (isLoadMore) {
            setOrders((prev) => [...prev, ...slice.map((row) => ({ ...row }))]);
          } else if (options.merge) {
            setOrders((prev) => mergeOrderPageIntoList(prev, slice));
          } else {
            setOrders(slice.map((row) => ({ ...row })));
          }
          setTotalPages(response.totalPages || 1);
          setTotalOrders(response.totalOrders || 0);
          hasLoadedOrdersOnceRef.current = true;
        } else {
          setError(response?.message || 'Failed to fetch orders');
        }
      } catch {
        if (generation === ordersFetchGenerationRef.current) {
          setError('An error occurred while fetching orders');
        }
      } finally {
        if (generation !== ordersFetchGenerationRef.current) return;
        setLoading(false);
        setIsLoadingMore(false);
        if (isLoadMore) {
          mobileInfiniteLockRef.current = false;
        }
      }
    },
    [limit, startDate, endDate, ordersListStatusParam, tableFilter]
  );

  useEffect(() => {
    const isInfiniteLoad =
      page > 1 && typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches;

    void refreshOrdersPage({
      targetPage: page,
      append: isInfiniteLoad,
      merge: false,
    });
  }, [page, limit, startDate, endDate, ordersListStatusParam, tableFilter, refreshTrigger, refreshOrdersPage]);

  const patchOrderInList = useCallback((orderId: string, patch: Partial<ServerOrder>) => {
    setOrders((prev) =>
      prev.map((row) => (row._id === orderId ? { ...row, ...patch } : row))
    );
  }, []);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    if (statusFilter) {
      localStorage.setItem('orderStatusFilter', statusFilter);
    } else {
      localStorage.removeItem('orderStatusFilter');
    }
  }, [statusFilter]);

  useEffect(() => {
    if (tableFilter) {
      localStorage.setItem('orderTableFilter', tableFilter);
    } else {
      localStorage.removeItem('orderTableFilter');
    }
  }, [tableFilter]);

  useEffect(() => {
    if (!canUseTableFilter && tableFilter) {
      setTableFilter('');
    }
  }, [canUseTableFilter, tableFilter]);


  // Infinite scroll for mobile - use refs to avoid dependency issues
  const scrollRef = React.useRef({
    isLoadingMore,
    page,
    totalPages,
    ordersLength: orders.length
  });

  // Update refs when values change
  React.useEffect(() => {
    scrollRef.current = {
      isLoadingMore,
      page,
      totalPages,
      ordersLength: orders.length
    };
  }, [isLoadingMore, page, totalPages, orders.length]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
    mobileInfiniteLockRef.current = false;
  }, [startDate, endDate, statusFilter, tableFilter]);

  // Allow external events (notification handlers) to request orders refresh.
  useEffect(() => {
    const handleOrdersRefreshRequested = () => {
      setPage(1);
      setRefreshTrigger(prev => prev + 1);
    };

    window.addEventListener('ordersRefreshRequested', handleOrdersRefreshRequested);
    return () => {
      window.removeEventListener('ordersRefreshRequested', handleOrdersRefreshRequested);
    };
  }, []);

  const lastProductTableTimestampRef = React.useRef<string | null>(null);

  // Poll product-table timestamp only on page 1 — avoids churn / duplicate fetches while infinite-scrolling.
  useEffect(() => {
    if (page !== 1) return;

    const shopId = getShopIdFromAuthToken();
    if (!shopId) return;

    const poll = async () => {
      try {
        const url = `${PRODUCT_TABLE_TIMESTAMP_URL}?${new URLSearchParams({ shopId }).toString()}`;
        const res = await fetch(url, { method: 'GET', headers: { Accept: 'application/json' } });
        if (!res.ok) return;
        const data = (await res.json()) as { timestamp?: string };
        const ts = typeof data.timestamp === 'string' ? data.timestamp : '';
        if (!ts) return;

        if (lastProductTableTimestampRef.current === null) {
          lastProductTableTimestampRef.current = ts;
          return;
        }
        if (ts !== lastProductTableTimestampRef.current) {
          lastProductTableTimestampRef.current = ts;
          setPage(1);
          setRefreshTrigger((prev) => prev + 1);
        }
      } catch {
        /* ignore */
      }
    };

    void poll();
    const intervalId = window.setInterval(poll, 3000);
    return () => window.clearInterval(intervalId);
  }, [page]);

  // Collapse filter box on mobile when any filter changes
  useEffect(() => {
    setShowMobileFilters(false);
  }, [startDate, endDate, statusFilter, tableFilter]);

  // Mobile infinite scroll: one IntersectionObserver; defer one frame so sentinel ref is committed.
  useLayoutEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (!window.matchMedia('(max-width: 767px)').matches) {
      return;
    }
    if (page >= totalPages || totalPages < 1) {
      return;
    }

    let cancelled = false;
    let observer: IntersectionObserver | null = null;

    const frameId = requestAnimationFrame(() => {
      if (cancelled) return;
      const el = mobileSentinelRef.current;
      if (!el) return;

      observer = new IntersectionObserver(
        (entries) => {
          const hit = entries.some((e) => e.isIntersecting);
          if (!hit) return;
          const { isLoadingMore: loadingMore, page: p, totalPages: tp, ordersLength } = scrollRef.current;
          if (mobileInfiniteLockRef.current || loadingMore) return;
          if (p >= tp || ordersLength < 1) return;
          mobileInfiniteLockRef.current = true;
          setPage((prev) => prev + 1);
        },
        { root: null, rootMargin: '200px', threshold: 0 }
      );
      observer.observe(el);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(frameId);
      observer?.disconnect();
    };
  }, [page, totalPages, orders.length]);

  const formatDate = (epochMillis: number) => {
    return new Date(epochMillis).toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const handleCustomerClick = async (customerId: string) => {
    if (!canViewCustomerUnpaidBills) return;
    setSelectedCustomer(customerId);
    setIsCustomerModalOpen(true);
    setCustomerOrdersLoading(true);
    
    try {
      const response = await apiService.getOrdersByCustomer(customerId, '0');
      if (response && response.status) {
        setCustomerOrders(response.data || []);
      } else {
        setError(response?.message || 'Failed to fetch customer orders');
        setCustomerOrders([]);
      }
    } catch (error) {
      setError('Failed to fetch customer orders');
      setCustomerOrders([]);
    } finally {
      setCustomerOrdersLoading(false);
    }
  };

  const handleTableClick = async (tableName: string) => {
    setSelectedTable(tableName);
    setIsTableModalOpen(true);
    setTableDetailsLoading(true);
    
    try {
      const response = await apiService.getTableSessions();
      if (response && response.status) {
        // Find the table with matching name
        const table = response.data.find((t: any) => t.name === tableName);
        setTableDetails(table || null);
      } else {
        setError(response?.message || 'Failed to fetch table details');
        setTableDetails(null);
      }
    } catch (error) {
      setError('Failed to fetch table details');
      setTableDetails(null);
    } finally {
      setTableDetailsLoading(false);
    }
  };

  const handleRemoveSession = (session: any) => {
    setSelectedSession(session);
    setIsRemoveSessionModalOpen(true);
  };

  const confirmRemoveSession = async () => {
    if (!selectedSession) return;

    setIsRemovingSession(true);
    setError('');
    try {
      const response = await apiService.updateSessionStatus(selectedSession._id, 'REMOVED');
      if (response && response.status) {
        // Show success toast
        showSuccess('User Removed Successfully', 'The user has been removed and the table is now available for new customers.');
        // Refresh table details
        await handleTableClick(selectedTable);
        setIsRemoveSessionModalOpen(false);
        setSelectedSession(null);
        setError('');
      } else {
        // Show error toast only
        showError('Failed to Remove User', response?.message || 'Failed to remove session');
      }
    } catch (err) {
      // Show error toast only
      showError('Failed to Remove User', 'An error occurred while removing the user');
    } finally {
      setIsRemovingSession(false);
    }
  };

  const calculateTotalAmount = (orders: ServerOrder[]) => {
    return orders.reduce((total, order) => total + order.totalAmount, 0);
  };

  const getRestaurantDetails = () => {
    try {
      const restaurantData = localStorage.getItem('restaurantDetails');
      if (restaurantData) {
        return JSON.parse(restaurantData);
      }
    } catch (error) {
      console.error('Error parsing restaurant details from localStorage:', error);
    }
    return null;
  };

  const openNewOrderModal = () => {
    setNewOrderChannel(readStoredNewOrderChannel());
    setNewOrderTableId(readStoredNewOrderTableId());
    setIsNewOrderModalOpen(true);
  };

  const handleOpenPublicMenuForNewOrder = () => {
    const restaurantDetails = getRestaurantDetails();
    const shopUsernameFromToken = getShopUsernameFromAuthToken();
    const shopKey =
      shopUsernameFromToken ||
      (restaurantDetails?.id as string | undefined) ||
      (restaurantDetails?._id as string | undefined) ||
      '';
    if (!shopKey || !String(shopKey).trim()) {
      showError('New order', 'Shop username is missing in auth token/profile. Please login again and try.');
      return;
    }
    const restaurantContextForUrl = {
      ...(restaurantDetails || {}),
      id: String(shopKey).trim(),
    };
    let url = '';
    if (newOrderChannel === 'table') {
      const table = tables.find((t) => t._id === newOrderTableId);
      if (!table) {
        showError('New order', 'Please select a table.');
        return;
      }
      url = buildRestaurantFrontendOrigin(restaurantContextForUrl, { tableName: table.name });
    } else {
      url = buildRestaurantFrontendOrigin(restaurantContextForUrl, { orderType: newOrderChannel });
    }

    setNewOrderPreviewUrl(url);
    setIsNewOrderPreviewModalOpen(true);
    setIsNewOrderModalOpen(false);
  };

  const formatCurrency = (amount: number) => `Rs ${Number(amount || 0).toFixed(2)}`;

  const getOrderSubtotal = (order: ServerOrder) =>
    order.products.reduce((sum, product) => sum + (product.offerPrice || 0) * (product.quantity || 0), 0);

  const getSafeHtml = (text: string | undefined | null) =>
    String(text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const openBillModal = (order: ServerOrder) => {
    if (!canViewBills) return;
    setSelectedBillOrder(order);
    setIsBillModalOpen(true);
  };

  const openKitchenItemsModal = (order: ServerOrder) => {
    setSelectedKitchenOrder(order);
    setIsKitchenItemsModalOpen(true);
  };

  const buildBillPrintHTML = (order: ServerOrder, format: BillPrintFormat) => {
    const restaurantDetails = getRestaurantDetails();
    const billDate = new Date();
    const subtotal = getOrderSubtotal(order);
    const total = Number(order.totalAmount || 0);
    const layout = getBillPrintLayout(format);

    return `
      <html>
        <head>
          <title>Bill ${getSafeHtml(order.invoiceNo || order._id || '')}</title>
          <style>
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              margin: 0;
              padding: ${layout.bodyPadding};
              color: #111827;
              font-size: ${layout.bodyFontSize};
              background: #ffffff;
            }
            .bill-wrap {
              max-width: ${layout.wrapMaxWidth};
              margin: 0 auto;
            }
            .text-center { text-align: center; }
            .muted { color: #6b7280; }
            .bold { font-weight: 700; }
            .mt-8 { margin-top: 8px; }
            .mt-12 { margin-top: 12px; }
            .mt-16 { margin-top: 16px; }
            .line {
              border-top: 1px dashed #9ca3af;
              margin: 8px 0;
            }
            .row {
              display: flex;
              justify-content: space-between;
              align-items: baseline;
              gap: 12px;
            }
            .item-name {
              flex: 1;
              margin-right: 8px;
              word-break: break-word;
            }
            .items-head {
              font-weight: 700;
              border-bottom: 1px solid #d1d5db;
              padding-bottom: 4px;
            }
            .total {
              font-weight: 800;
              font-size: ${layout.totalFontSize};
            }
            @media print {
              @page {
                margin: ${layout.pageMargin};
                size: ${layout.pageSize};
              }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="bill-wrap">
            <div class="text-center">
              <div class="bold" style="font-size:${layout.titleFontSize}">${getSafeHtml(restaurantDetails?.name || 'Restaurant')}</div>
              ${restaurantDetails?.address ? `<div class="muted mt-8">${getSafeHtml(restaurantDetails.address)}</div>` : ''}
              ${restaurantDetails?.phone ? `<div class="muted">${getSafeHtml(restaurantDetails.phone)}</div>` : ''}
              ${restaurantDetails?.email ? `<div class="muted">${getSafeHtml(restaurantDetails.email)}</div>` : ''}
            </div>
            <div class="line"></div>
            <div class="row"><span>Invoice</span><span class="bold">${getSafeHtml(order.invoiceNo || order.referenceId || order._id || '-')}</span></div>
            <div class="row"><span>Date</span><span>${billDate.toLocaleDateString('en-IN')}</span></div>
            <div class="row"><span>Time</span><span>${billDate.toLocaleTimeString('en-IN')}</span></div>
            ${order.table ? `<div class="row"><span>Table</span><span>${getSafeHtml(order.table)}</span></div>` : ''}
            ${order.createdBy ? `<div class="row"><span>Customer</span><span>${getSafeHtml(order.createdBy)}</span></div>` : ''}
            <div class="line"></div>
            <div class="row items-head"><span class="item-name">Item</span><span>Qty</span><span>Rate</span><span>Amt</span></div>
            ${order.products.map((item) => `
              <div class="row mt-8">
                <span class="item-name">${getSafeHtml(item.name)}</span>
                <span>${item.quantity}</span>
                <span>${formatCurrency(item.offerPrice)}</span>
                <span>${formatCurrency(item.offerPrice * item.quantity)}</span>
              </div>
            `).join('')}
            <div class="line mt-12"></div>
            <div class="row"><span class="muted">Subtotal</span><span>${formatCurrency(subtotal)}</span></div>
            <div class="row total mt-8"><span>Total</span><span>${formatCurrency(total)}</span></div>
            <div class="line mt-12"></div>
            <div class="text-center muted mt-8">Thank you for your order</div>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrintOrderBill = (order: ServerOrder, format: BillPrintFormat) => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const html = buildBillPrintHTML(order, format);
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  const isOnlinePaymentMethod = (order: ServerOrder) => String(order.paymentMethod) === '0';

  const getPaymentMethodLabel = (method: ServerOrder['paymentMethod']) => {
    const normalized = String(method ?? '');
    if (normalized === '0') return 'Online';
    if (normalized === '1') return 'UPI';
    if (normalized === '2') return 'Cash';
    if (normalized === '3') return 'Card';
    if (normalized === '4') return 'Credit (Due)';
    return 'Not Specified';
  };

  const getPaymentStatusLabel = (status: ServerOrder['paymentStatus']) => {
    const normalized = String(status ?? '');
    if (normalized === '1') return 'Paid';
    if (normalized === '0') return 'Pending';
    return 'Not Specified';
  };

  const paymentMethodLabels: Record<PaymentOption, string> = {
    cash: 'Cash',
    card: 'Card',
    upi: 'UPI',
    online: 'Online',
    credit_due: 'Credit (Due)',
  };

  const paymentMethodApiCodes: Record<PaymentOption, string> = {
    cash: '2',
    card: '3',
    upi: '1',
    online: '0',
    credit_due: '4',
  };

  const handlePaymentMethodSelectionChange = (method: PaymentOption) => {
    setPaymentMethodSelection(method);
    persistMarkPaidPaymentMethod(method);
  };

  const openPaymentModal = (order: ServerOrder) => {
    if (!canMarkPayment) return;
    setSelectedOrder(order);
    setPaymentMethodSelection(readStoredMarkPaidPaymentMethod());
    setPaymentReferenceId('');
    setCashReceivedAmount('');
    setPaymentNote('');
    setError('');
    setIsPaymentModalOpen(true);
  };

  const closePaymentModal = () => {
    setIsPaymentModalOpen(false);
    setSelectedOrder(null);
    setPaymentReferenceId('');
    setCashReceivedAmount('');
    setPaymentNote('');
  };

  const parsedCashReceivedAmount = Number(cashReceivedAmount || 0);
  const selectedOrderTotal = Number(selectedOrder?.totalAmount || 0);
  const calculatedChangeAmount =
    paymentMethodSelection === 'cash' && parsedCashReceivedAmount > 0
      ? Math.max(parsedCashReceivedAmount - selectedOrderTotal, 0)
      : 0;

  const handleOpenPaymentLink = (order: ServerOrder) => {
    if (!order.paymentLink) {
      showError('Payment', 'Payment link is not available for this order');
      return;
    }
    window.open(order.paymentLink, '_blank', 'noopener,noreferrer');
  };

  const renderOrderPaymentLinkBadge = (order: ServerOrder, variant: 'paid' | 'unpaid') => {
    const label = variant === 'paid' ? 'Paid' : 'Unpaid';
    const className =
      variant === 'paid'
        ? 'inline-flex items-center justify-center rounded-md border border-green-300 bg-green-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-green-800'
        : 'inline-flex items-center justify-center rounded-md border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide text-amber-800';
    const tooltip =
      variant === 'paid' ? 'Click to view receipt' : 'Click to verify live status';
    const canOpenLink = isOnlinePaymentMethod(order);

    if (canOpenLink) {
      return (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleOpenPaymentLink(order);
          }}
          className={`${className} ${variant === 'paid' ? 'hover:bg-green-100' : 'hover:bg-amber-100'} cursor-pointer`}
          title={tooltip}
        >
          {label}
        </button>
      );
    }

    return <span className={`${className} cursor-default`}>{label}</span>;
  };

  const buildCustomerBillPrintHTML = (format: BillPrintFormat) => {
    const restaurantDetails = getRestaurantDetails();
    const totalAmount = calculateTotalAmount(customerOrders);
    const layout = getBillPrintLayout(format);

    return `
      <html>
        <head>
          <title>Unpaid Bill ${getSafeHtml(selectedCustomer || '')}</title>
          <style>
            body {
              font-family: 'Segoe UI', Arial, sans-serif;
              margin: 0;
              padding: ${layout.bodyPadding};
              color: #111827;
              font-size: ${layout.bodyFontSize};
              background: #ffffff;
            }
            .bill-wrap { max-width: ${layout.wrapMaxWidth}; margin: 0 auto; }
            .line { border-top: 1px dashed #9ca3af; margin: 8px 0; }
            .row { display: flex; justify-content: space-between; gap: 8px; }
            .muted { color: #6b7280; }
            .bold { font-weight: 700; }
            .mt-8 { margin-top: 8px; }
            .mt-12 { margin-top: 12px; }
            .items-head { font-weight: 700; border-bottom: 1px solid #d1d5db; padding-bottom: 4px; }
            .order-block { margin-bottom: 14px; border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px; }
            .total { font-weight: 800; font-size: ${layout.totalFontSize}; }
            @media print {
              @page { margin: ${layout.pageMargin}; size: ${layout.pageSize}; }
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>
          <div class="bill-wrap">
            <div style="text-align:center">
              <div class="bold" style="font-size:${layout.titleFontSize}">${getSafeHtml(restaurantDetails?.name || 'Restaurant')}</div>
              ${restaurantDetails?.address ? `<div class="muted mt-8">${getSafeHtml(restaurantDetails.address)}</div>` : ''}
              ${restaurantDetails?.phone ? `<div class="muted">${getSafeHtml(restaurantDetails.phone)}</div>` : ''}
              ${restaurantDetails?.email ? `<div class="muted">${getSafeHtml(restaurantDetails.email)}</div>` : ''}
            </div>
            <div class="line"></div>
            <div class="row"><span>Customer</span><span class="bold">${getSafeHtml(selectedCustomer || '-')}</span></div>
            <div class="row"><span>Date</span><span>${new Date().toLocaleDateString('en-IN')}</span></div>
            <div class="row"><span>Time</span><span>${new Date().toLocaleTimeString('en-IN')}</span></div>
            <div class="line"></div>
            ${customerOrders.length === 0 ? `
              <div class="muted" style="text-align:center;padding:12px 0;">No unpaid orders found.</div>
            ` : customerOrders.map((order, index) => `
              <div class="order-block">
                <div class="row"><span class="bold">Order #${index + 1}</span><span class="bold">${getSafeHtml(order.invoiceNo || order.referenceId || order._id)}</span></div>
                <div class="row muted mt-8"><span>${formatDate(order.createdAt)}</span><span>${order.table ? `Table ${getSafeHtml(order.table)}` : ''}</span></div>
                <div class="line mt-8"></div>
                <div class="row items-head"><span>Item</span><span>Qty</span><span>Amt</span></div>
                ${order.products.map((item) => `
                  <div class="row mt-8"><span>${getSafeHtml(item.name)}</span><span>${item.quantity}</span><span>${formatCurrency(item.offerPrice * item.quantity)}</span></div>
                `).join('')}
                <div class="line mt-8"></div>
                <div class="row total"><span>Order Total</span><span>${formatCurrency(order.totalAmount)}</span></div>
              </div>
            `).join('')}
            <div class="line mt-12"></div>
            <div class="row total"><span>Grand Total</span><span>${formatCurrency(totalAmount)}</span></div>
          </div>
        </body>
      </html>
    `;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const printHTML = buildCustomerBillPrintHTML(billPrintFormat);
    printWindow.document.write(printHTML);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const handleSendPaymentLink = async () => {
    if (!selectedCustomer || customerOrders.length === 0) {
      showError('Payment Link', 'No unpaid orders available for this customer');
      return;
    }

    const referenceIds = customerOrders
      .map((order) => (order.referenceId || '').trim())
      .filter((referenceId) => referenceId.length > 0);

    if (referenceIds.length === 0) {
      showError('Payment Link', 'Reference ID is missing for unpaid orders');
      return;
    }

    setIsSendingPaymentLink(true);
    try {
      const totalAmount = Number(calculateTotalAmount(customerOrders) || 0);
      const payload = {
        referenceId: referenceIds.join('_'),
        from: selectedCustomer,
        totalAmount,
      };

      const response = await apiService.createPaymentLink(payload);
      if (response?.status) {
        showSuccess('Payment Link', 'Payment link sent successfully');
      } else {
        showError('Payment Link', response?.message || 'Failed to create payment link');
      }
    } catch (err) {
      showError('Payment Link', 'Failed to create payment link');
    } finally {
      setIsSendingPaymentLink(false);
    }
  };

  // Helper function to convert date to start/end of day with timezone
  const getDateRange = (startDate: string, endDate: string) => {
    let startParam = '';
    let endParam = '';

    if (startDate) {
      // Set to beginning of the day (00:00:00) in local timezone
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      startParam = start.toISOString();
    }

    if (endDate) {
      // Set to end of the day (23:59:59.999) in local timezone
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      endParam = end.toISOString();
    }

    return { startParam, endParam };
  };

  /** Next step in the happy path: New → Ready → Picked Up (delivery only) → Delivered. */
  /** Primary action copy for list / modal confirm (matches target status being applied). */
  const getStatusTransitionActionLabel = (targetStatus: string): string => {
    const s = String(targetStatus ?? '').trim();
    if (s === 'Ready') return 'Mark Ready';
    if (s === 'Picked Up') return 'Pick Up Completed';
    if (s === 'Delivered') return 'Delivery Completed';
    return 'Confirm';
  };

  const getSuggestedNextStatus = (order: ServerOrder): string => {
    const current = String(order.status ?? '').trim();
    if (current === 'New' || current === '') return 'Ready';
    if (current === 'Ready') return isDeliveryOrder(order) ? 'Picked Up' : 'Delivered';
    if (current === 'Picked Up') return 'Delivered';
    if (current === 'Delivered') return 'Delivered';
    return 'Ready';
  };

  const shouldRequirePaymentBeforeDelivery = (order: ServerOrder, targetStatus: string): boolean => {
    return (
      String(order.status ?? '').trim() === 'Picked Up' &&
      targetStatus === 'Delivered' &&
      staffRole !== 'shopOwner' &&
      staffRole !== 'manager' &&
      !isOrderPaid(order)
    );
  };

  const handleOrderStatusAction = (order: ServerOrder) => {
    const nextStatus = getSuggestedNextStatus(order);
    if (shouldRequirePaymentBeforeDelivery(order, nextStatus)) {
      setIsStatusModalOpen(false);
      setStatusComment('');
      setError('');
      openPaymentModal(order);
      return;
    }
    setSelectedOrder(order);
    setNewStatus(getInitialNewStatusForModal(order));
    setStatusComment('');
    setError('');
    setIsStatusModalOpen(true);
  };

  const getInitialNewStatusForModal = (order: ServerOrder): string => {
    const suggested = getSuggestedNextStatus(order);
    if (isStatusUpdateAllowed(order, suggested)) return suggested;

    const current = String(order.status ?? '').trim();
    const allowed = allowedStatusTargets.filter((s) => isStatusUpdateAllowed(order, s));
    if (allowed.includes(current)) return current;

    const flow = isDeliveryOrder(order)
      ? (['New', 'Ready', 'Picked Up', 'Delivered'] as const)
      : (['New', 'Ready', 'Delivered'] as const);
    const idx = (flow as readonly string[]).indexOf(current);
    const start = idx >= 0 ? idx + 1 : 0;
    for (let i = start; i < flow.length; i++) {
      const s = flow[i];
      if (isStatusUpdateAllowed(order, s)) return s;
    }
    return allowed[0] || suggested;
  };

  const handleStatusUpdate = async () => {
    if (!selectedOrder) return;

    if (!isStatusUpdateAllowed(selectedOrder, newStatus)) {
      if (shouldRequirePaymentBeforeDelivery(selectedOrder, newStatus)) {
        setIsStatusModalOpen(false);
        setStatusComment('');
        setError('');
        openPaymentModal(selectedOrder);
        return;
      }
      setError('You are not allowed to update this order status.');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await apiService.updateOrderStatus(
        selectedOrder._id,
        newStatus,
        statusComment,
        { referenceId: selectedOrder.referenceId || selectedOrder.invoiceNo || selectedOrder._id }
      );
      if (response && response.status) {
        patchOrderInList(selectedOrder._id, {
          status: newStatus,
          ...(statusComment.trim() ? { comment: statusComment.trim() } : {}),
        });
        await refreshOrdersPage({ targetPage: page, merge: true });
        setIsStatusModalOpen(false);
        setSelectedOrder(null);
        setStatusComment('');
        setError('');
      } else {
        setError(response?.message || 'Failed to update order status');
      }
    } catch (err) {
      setError('Failed to update order status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleKitchenMarkReady = async () => {
    if (!selectedKitchenOrder || !isKitchenStaff) return;

    if (!isStatusUpdateAllowed(selectedKitchenOrder, 'Ready')) {
      setError('You are not allowed to update this order status.');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await apiService.updateOrderStatus(
        selectedKitchenOrder._id,
        'Ready',
        '',
        { referenceId: selectedKitchenOrder.referenceId || selectedKitchenOrder.invoiceNo || selectedKitchenOrder._id }
      );

      if (response && response.status) {
        patchOrderInList(selectedKitchenOrder._id, { status: 'Ready' });
        await refreshOrdersPage({ targetPage: page, merge: true });
        setIsKitchenItemsModalOpen(false);
        setSelectedKitchenOrder(null);
        setError('');
      } else {
        setError(response?.message || 'Failed to update order status');
      }
    } catch (err) {
      setError('Failed to update order status');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!selectedOrder) return;

    if (!canCancelOrder(selectedOrder)) {
      setError('You are not allowed to cancel this order.');
      return;
    }

    setIsUpdating(true);
    try {
      const response = await apiService.updateOrderStatus(
        selectedOrder._id,
        'Cancelled',
        statusComment,
        { referenceId: selectedOrder.referenceId || selectedOrder.invoiceNo || selectedOrder._id,from:selectedOrder.user+"" }
      );
      if (response && response.status) {
        patchOrderInList(selectedOrder._id, {
          status: 'Cancelled',
          ...(statusComment.trim() ? { comment: statusComment.trim() } : {}),
        });
        await refreshOrdersPage({ targetPage: page, merge: true });
        setIsCancelModalOpen(false);
        setSelectedOrder(null);
        setStatusComment('');
        setError('');
      } else {
        setError(response?.message || 'Failed to cancel order');
      }
    } catch (err) {
      setError('Failed to cancel order');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleMarkAsPaid = async () => {
    if (!selectedOrder) return;
    if (!canMarkPayment) {
      setError('You are not allowed to mark payment.');
      return;
    }

    if (paymentMethodSelection === 'cash' && cashReceivedAmount) {
      const cashValue = Number(cashReceivedAmount);
      if (Number.isNaN(cashValue) || cashValue <= 0) {
        setError('Please enter a valid received amount');
        return;
      }
    }

    setIsUpdating(true);
    setError('');
    try {
      const paymentPayload = {
        referenceId: selectedOrder.referenceId || selectedOrder.invoiceNo || selectedOrder._id,
        paymentStatus: '1',
        paymentMethod: paymentMethodApiCodes[paymentMethodSelection],
        paymentNote: paymentNote.trim(),
      };

      const response = await apiService.updateOrderPayment(selectedOrder._id, paymentPayload);

      if (response && response.status) {
        patchOrderInList(selectedOrder._id, {
          paymentStatus: 1,
          paymentMethod: paymentMethodApiCodes[paymentMethodSelection],
          paymentNote: paymentNote.trim(),
        });
        await refreshOrdersPage({ targetPage: page, merge: true });
        persistMarkPaidPaymentMethod(paymentMethodSelection);
        showSuccess('Payment', 'Order marked as paid successfully');
        closePaymentModal();
      } else {
        setError(response?.message || 'Failed to mark order as paid');
      }
    } catch (err) {
      setError('Failed to mark order as paid');
    } finally {
      setIsUpdating(false);
    }
  };

  if (loading && orders.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className={`flex flex-col ${orders.length > 0 ? 'md:pb-[6rem]' : ''}`}>
      <div className="flex flex-col max-md:gap-6 md:gap-4">
      {/* Mobile: filter toggle, new order, refresh (hidden md+) */}
      {canUseOrderFilters && (
        <div className="block md:hidden">
          <div className="flex gap-2">
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex-1 min-w-0 px-4 py-3 bg-white border border-gray-300 rounded-lg shadow-sm text-left flex items-center justify-between hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-pink-600"
            >
              <span className="text-sm font-medium text-gray-700">
                {startDate || endDate || statusFilter || tableFilter ? 'Filters Applied' : 'Filters'}
              </span>
              <svg
                className={`h-5 w-5 text-gray-400 transition-transform ${showMobileFilters ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {canCreateOrder && (
              <button
                type="button"
                onClick={openNewOrderModal}
                className="shrink-0 bg-orange-600 text-white px-3 py-3 rounded-lg shadow-sm hover:bg-orange-700 transition-colors flex items-center justify-center"
                title="New order"
              >
                <PlusIcon className="w-5 h-5" />
              </button>
            )}
            <button
              onClick={() => {
                setPage(1);
                setRefreshTrigger(prev => prev + 1);
              }}
              className="shrink-0 bg-orange-600 text-white px-4 py-3 rounded-lg shadow-sm hover:bg-orange-700 transition-colors flex items-center justify-center"
              title="Refresh"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          </div>
        </div>
      )}

      

      {/* Date Filters - Hidden on mobile unless button clicked */}
      {canUseOrderFilters && (
      <div className={`bg-white rounded-lg shadow-sm p-4 ${showMobileFilters ? 'block' : 'hidden md:block'}`}>
        <div className="space-y-4">
          {/* Mobile-first responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="space-y-2">
              <label htmlFor="startDate" className="block text-sm font-medium text-gray-700">
                Start Date
              </label>
              <input
                id="startDate"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="endDate" className="block text-sm font-medium text-gray-700">
                End Date
              </label>
              <input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600"
              />
            </div>
            <div className="space-y-2">
              <label htmlFor="statusFilter" className="block text-sm font-medium text-gray-700">
                Status
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600"
              >
                {canSeeAllStatuses && <option value="">All Status</option>}
                {allowedStatusFilterValues.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
          </select>
            </div>
            {canUseTableFilter && (
              <div className="space-y-2">
                <label htmlFor="tableFilter" className="block text-sm font-medium text-gray-700">
                  Table
                </label>
                <select
                  id="tableFilter"
                  value={tableFilter}
                  onChange={(e) => {
                    const tableId = e.target.value;
                    setTableFilter(tableId);
                    // Trigger refresh when table changes
                    setPage(1);
                    setRefreshTrigger(prev => prev + 1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600"
                  disabled={tablesLoading}
                >
                  <option value="">All Tables</option>
                  {tables.map((table) => (
                    <option key={table._id} value={table._id}>
                      {table.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700 opacity-0">
                Actions
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:flex lg:flex-wrap gap-2">
                <button
                  onClick={() => {
                    setStartDate(getTodayDateString());
                    setEndDate(getTodayDateString());
                    setStatusFilter(canSeeAllStatuses ? '' : (allowedStatusFilterValues[0] || ''));
                    setTableFilter('');
                    setPage(1);
                  }}
                  className="inline-flex w-full sm:w-auto items-center justify-center px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                >
                  Clear Filters
                </button>
                {canCreateOrder && (
                  <button
                    type="button"
                    onClick={openNewOrderModal}
                    className="hidden md:inline-flex w-full sm:w-auto items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-orange-600 border border-orange-600 rounded-lg shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                  >
                    <PlusIcon className="w-4 h-4 mr-2" />
                    New order
                  </button>
                )}
                <button
                  onClick={() => {
                    setPage(1);
                    setRefreshTrigger(prev => prev + 1);
                  }}
                  className="hidden md:inline-flex lg:hidden w-full sm:w-auto items-center justify-center px-4 py-2.5 text-sm font-semibold text-white bg-orange-600 border border-orange-600 rounded-lg shadow-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-pink-600 transition-colors"
                  title="Refresh"
                >
                  <ArrowPathIcon className="w-4 h-4 mr-2" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Mobile Card Layout */}
      <div className="block md:hidden space-y-3 max-md:pt-1">
        {orders.map((order) => (
          <div
            key={order._id}
            className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm"
          >
            <div className="space-y-3 p-4">
            <div className="flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
              <div className="min-w-0 flex-1 space-y-1">
                <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Order</p>
                <p className="text-sm font-semibold leading-snug text-gray-900 break-all">
                  {order.referenceId || order.invoiceNo || order._id || '—'}
                </p>
                {order.invoiceNo &&
                  order.referenceId &&
                  order.invoiceNo.trim() !== order.referenceId.trim() && (
                    <p className="text-xs text-gray-500">
                      Invoice No:{' '}
                      <span className="font-medium text-gray-700">{order.invoiceNo}</span>
                    </p>
                  )}
                <p className="text-xs text-gray-500">{formatDate(order.createdAt)}</p>
                {order.table ? (
                  <p 
                    className="text-xs font-medium text-orange-600 cursor-pointer hover:text-orange-800 hover:underline"
                    onClick={() => handleTableClick(order.table!)}
                  >
                    Table: {order.table}
                  </p>
                ) : getOrderTypeLabel(order) ? (
                  <p className="text-xs font-medium text-gray-600">
                    {getOrderTypeLabel(order)}
                  </p>
                ) : null}
                {canShowCustomerColumn && hasOrderCustomerColumnContent(order) && (
                  <OrderCustomerMobile
                    value={getOrderCustomerMobile(order)}
                    deliveryAddress={getOrderDeliveryAddress(order)}
                    canOpenCustomerBills={canViewCustomerUnpaidBills}
                    onOpenCustomerBills={handleCustomerClick}
                    variant="inline"
                  />
                )}
              </div>
              <div className="min-w-0 max-w-[48%] shrink-0 text-right">
                {canViewAmount && (
                  <>
                    <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Total</p>
                    <p className="text-base font-bold text-gray-900">₹{order.totalAmount}</p>
                    <p className="mt-1 inline-flex max-w-full flex-wrap items-center justify-end gap-1 text-[11px] leading-relaxed text-gray-500">
                      <span>
                        <span className="font-medium text-gray-600">Payment:</span>{' '}
                        <span>{getPaymentMethodLabel(order.paymentMethod)}</span>
                        <span className="mx-0.5">·</span>
                        <span>{getPaymentStatusLabel(order.paymentStatus)}</span>
                      </span>
                      {getOrderPaymentUpdatedBy(order) && (
                        <OrderUpdatedByTooltip
                          heading="Payment updated by"
                          updatedBy={getOrderPaymentUpdatedBy(order)!}
                          align="end"
                        />
                      )}
                    </p>
                    {getOrderPaymentNote(order) && (
                      <p
                        className="mt-1 max-w-[12rem] text-right text-[11px] leading-snug text-gray-600 line-clamp-3"
                        title={getOrderPaymentNote(order)!}
                      >
                        <span className="font-medium text-gray-700">Note:</span>{' '}
                        {getOrderPaymentNote(order)}
                      </p>
                    )}
                  </>
                )}
                <div className="mt-2 flex flex-wrap items-center justify-end gap-1.5">
                  {canViewAmount && !isOrderPaid(order) && renderOrderPaymentLinkBadge(order, 'unpaid')}
                  {canViewAmount && isOrderPaid(order) && renderOrderPaymentLinkBadge(order, 'paid')}
                  {order.status && (
                    <div className="inline-flex items-center gap-1">
                      <span
                        className={`inline-flex w-fit px-2 py-1 text-xs font-semibold rounded-full ${getOrderStatusBadgeClassName(order.status)}${
                          isTerminalOrderStatus(order) ? ' select-none touch-none' : ''
                        }`}
                        {...getTerminalStatusBadgeInteractionProps(order)}
                      >
                        {order.status}
                      </span>
                      {getOrderStatusUpdatedBy(order) && (
                        <OrderUpdatedByTooltip
                          heading="Status updated by"
                          updatedBy={getOrderStatusUpdatedBy(order)!}
                          align="end"
                        />
                      )}
                    </div>
                  )}
                </div>
                {order.comment && (
                  <div className="mt-1 max-w-[200px] truncate text-xs text-gray-500">
                    {order.comment}
                  </div>
                )}
              </div>
            </div>
            
            <div
              className="rounded-lg border border-gray-100 bg-gray-50/50 p-3 cursor-pointer transition-colors hover:border-orange-200 hover:bg-orange-50/30 outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
              onClick={() => openKitchenItemsModal(order)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  openKitchenItemsModal(order);
                }
              }}
            >
              <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Products</h4>
              <div className="space-y-1.5">
                {order.products.map((product, index) => (
                  <div key={index} className="flex justify-between items-center text-sm">
                    <span className="text-gray-900">{product.name}</span>
                    <span className="text-gray-500">
                      ×{product.quantity} - ₹{product.offerPrice}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="space-y-2 border-t border-gray-100 pt-3">
              <div className="flex flex-wrap gap-2">
                {canViewBills && (
                  <button
                    type="button"
                    onClick={() => openBillModal(order)}
                    className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400"
                    title="View Bill"
                  >
                    <DocumentTextIcon className="h-4 w-4" />
                  </button>
                )}
                {canMarkPayment && (
                  <button
                    type="button"
                    onClick={() => openPaymentModal(order)}
                    disabled={isOrderPaid(order)}
                    className="inline-flex h-9 flex-1 min-w-[7rem] items-center justify-center rounded-lg border border-green-200 bg-green-50 px-3 text-xs font-semibold text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Mark Paid
                  </button>
                )}
              </div>
              {showUpdateStatusButton(order) && (
                <button
                  type="button"
                  aria-label={getStatusTransitionActionLabel(getSuggestedNextStatus(order))}
                  onClick={() => handleOrderStatusAction(order)}
                  className="inline-flex h-10 w-full items-center justify-center rounded-lg bg-orange-600 px-3 text-sm font-semibold text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <PencilIcon className="mr-2 h-4 w-4 shrink-0" />
                  {getStatusTransitionActionLabel(getSuggestedNextStatus(order))}
                </button>
              )}
              {showCancelOrderButton(order) && (
                <div className="flex justify-end pt-0.5">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedOrder(order);
                      setIsCancelModalOpen(true);
                    }}
                    className="inline-flex h-7 items-center rounded-md border border-gray-200 bg-white px-2.5 text-[11px] font-medium text-gray-600 hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus:ring-2 focus:ring-red-200"
                  >
                    Cancel order
                  </button>
                </div>
              )}
            </div>
            </div>
          </div>
        ))}
        
        {/* Infinite scroll sentinel - only show on mobile when there are more pages */}
        {page < totalPages && (
          <div
            id="infinite-scroll-sentinel"
            ref={mobileSentinelRef}
            className="h-4 w-full shrink-0"
            aria-hidden
          />
        )}
      </div>

      {/* Desktop Table Layout */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Invoice No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Table
                </th>
                {canShowCustomerColumn && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Customer
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Products
                </th>
                {canViewAmount && (
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total Amount
                  </th>
                )}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.map((order) => (
                <tr key={order._id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">{order.referenceId}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDate(order.createdAt)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {order.table ? (
                        <span 
                          className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 cursor-pointer hover:bg-orange-200 hover:text-orange-900"
                          onClick={() => handleTableClick(order.table!)}
                        >
                          {order.table}
                        </span>
                      ) : getOrderTypeLabel(order) ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-slate-100 text-slate-700">
                          {getOrderTypeLabel(order)}
                        </span>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  {canShowCustomerColumn && (
                    <td className="max-w-[12rem] px-6 py-4 align-top">
                      {hasOrderCustomerColumnContent(order) ? (
                        <OrderCustomerMobile
                          value={getOrderCustomerMobile(order)}
                          deliveryAddress={getOrderDeliveryAddress(order)}
                          canOpenCustomerBills={canViewCustomerUnpaidBills}
                          onOpenCustomerBills={handleCustomerClick}
                          variant="table"
                        />
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                  )}
                  <td
                    className="px-6 py-4 cursor-pointer align-top text-sm text-gray-900 hover:bg-orange-50/50 transition-colors"
                    onClick={() => openKitchenItemsModal(order)}
                  >
                    <div>
                      {order.products.map((product, index) => (
                        <div key={index} className="mb-1">
                          <span>{product.name}</span>
                          <span className="text-gray-500 ml-2">
                            ×{product.quantity} - ₹{product.offerPrice}
                          </span>
                        </div>
                      ))}
                    </div>
                  </td>
                  {canViewAmount && (
                    <td className="max-w-[14rem] px-6 py-4 align-top">
                      <div className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900">₹{order.totalAmount}</div>
                          <div className="mt-1 flex flex-wrap items-center gap-1 text-xs text-gray-600">
                            <span>
                              <span className="font-medium text-gray-700">Payment:</span>{' '}
                              <span>{getPaymentMethodLabel(order.paymentMethod)}</span>
                              <span className="mx-1">|</span>
                              <span>{getPaymentStatusLabel(order.paymentStatus)}</span>
                            </span>
                            {getOrderPaymentUpdatedBy(order) && (
                              <OrderUpdatedByTooltip
                                heading="Payment updated by"
                                updatedBy={getOrderPaymentUpdatedBy(order)!}
                              />
                            )}
                          </div>
                          {getOrderPaymentNote(order) && (
                            <p
                              className="mt-1 text-xs leading-snug text-gray-600 line-clamp-3"
                              title={getOrderPaymentNote(order)!}
                            >
                              <span className="font-medium text-gray-700">Note:</span>{' '}
                              {getOrderPaymentNote(order)}
                            </p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          {!isOrderPaid(order) && renderOrderPaymentLinkBadge(order, 'unpaid')}
                          {isOrderPaid(order) && renderOrderPaymentLinkBadge(order, 'paid')}
                          {/* {isOnlinePaymentMethod(order) && isPaymentPending(order) && (
                            <button
                              type="button"
                              onClick={() => handleOpenPaymentLink(order)}
                              className="inline-flex items-center justify-center px-3 py-1.5 rounded-md border border-blue-300 bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100"
                              title="Open payment link"
                            >
                              Check Payment Status
                            </button>
                          )} */}
                        </div>
                      </div>
                    </td>
                  )}
                  <td className="px-6 py-4 align-top">
                    {order.status && (
                      <div className="flex flex-col">
                        <div className="inline-flex items-center gap-1">
                          <span
                            className={`inline-flex w-auto px-2 py-1 text-xs font-semibold rounded-full w-fit ${getOrderStatusBadgeClassName(order.status)}${
                              isTerminalOrderStatus(order) ? ' select-none touch-none' : ''
                            }`}
                            {...getTerminalStatusBadgeInteractionProps(order)}
                          >
                            {order.status}
                          </span>
                          {getOrderStatusUpdatedBy(order) && (
                            <OrderUpdatedByTooltip
                              heading="Status updated by"
                              updatedBy={getOrderStatusUpdatedBy(order)!}
                            />
                          )}
                        </div>
                        {order.comment && (
                          <span className="mt-1 max-w-xs truncate text-xs text-gray-500" title={order.comment}>
                            {order.comment}
                          </span>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex flex-wrap gap-2">
                      {canViewBills && (
                        <button
                          onClick={() => openBillModal(order)}
                          className="inline-flex items-center p-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors"
                          title="View Bill"
                        >
                          <DocumentTextIcon className="h-4 w-4" />
                        </button>
                      )}
                      {canMarkPayment && (
                        <button
                          type="button"
                          onClick={() => openPaymentModal(order)}
                          disabled={isOrderPaid(order)}
                          className="inline-flex items-center whitespace-nowrap rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs font-semibold text-green-800 hover:bg-green-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                          title="Mark as Paid"
                        >
                          Mark Paid
                        </button>
                      )}
                      {showUpdateStatusButton(order) && (
                        <button
                        onClick={() => handleOrderStatusAction(order)}
                        className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-600 transition-colors"
                          title={getStatusTransitionActionLabel(getSuggestedNextStatus(order))}
                          aria-label={getStatusTransitionActionLabel(getSuggestedNextStatus(order))}
                        >
                          <PencilIcon className="h-4 w-4" />
                        </button>
                      )}
                      {showCancelOrderButton(order) && (
                        <button
                        onClick={() => {
                          setSelectedOrder(order);
                          setIsCancelModalOpen(true);
                        }}
                        className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
                          title="Cancel Order"
                        >
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {orders.length === 0 && !loading && (
        <div className="flex min-h-[calc(100dvh-14rem)] flex-col items-center justify-center px-4 py-12 text-center md:min-h-[calc(100dvh-12rem)]">
          <p className="max-w-md text-base text-gray-600 md:text-lg">
            {ordersHeadingParts.emphasis ? (
              <>
                No{' '}
                <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text font-semibold text-transparent">
                  {ordersHeadingParts.emphasis}
                </span>{' '}
                orders found.
              </>
            ) : (
              <>No orders found.</>
            )}
          </p>
        </div>
      )}

      {/* Mobile Loading Indicator */}
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      )}

      {/* Mobile Total Count - Always visible */}
      {orders.length > 0 && (
        <div className="block md:hidden text-center py-4">
          <span className="text-sm text-gray-600">Total: {totalOrders} orders</span>
        </div>
      )}
      </div>

      {/* Desktop: fixed to viewport bottom; lg+ left offset tracks collapsible sidebar */}
      {orders.length > 0 && (
        <div
          className={`hidden md:block md:fixed md:bottom-0 md:right-0 md:z-30 md:border-t md:border-gray-200 md:bg-white/95 md:py-3 md:px-4 md:shadow-[0_-8px_30px_rgba(15,23,42,0.06)] md:backdrop-blur-sm md:left-0 lg:px-6 supports-[backdrop-filter]:bg-white/80 transition-[left] duration-200 ease-in-out ${
            desktopSidebarCollapsed ? 'lg:left-0' : 'lg:left-64'
          }`}
          role="navigation"
          aria-label="Orders pagination"
        >
          <div className="mx-auto flex max-w-[1600px] flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
              <p className="text-sm text-gray-600">
                <span className="font-semibold tabular-nums text-gray-900">{totalOrders.toLocaleString()}</span>
                <span className="text-gray-500"> orders total</span>
              </p>
              <div className="hidden h-4 w-px bg-gray-200 sm:block" aria-hidden />
              <div className="flex items-center gap-2">
                <label htmlFor="orders-page-size" className="text-sm font-medium text-gray-700">
                  Rows per page
                </label>
                <select
                  id="orders-page-size"
                  value={limit}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-900 shadow-sm transition-colors hover:border-gray-300 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/25"
                >
                  {pageSizeOptions.map((size) => (
                    <option key={size} value={size}>
                      {size}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-end">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:shadow-none"
              >
                <ChevronLeftIcon className="h-4 w-4 shrink-0" aria-hidden />
                Previous
              </button>
              <span className="min-w-[9rem] px-2 text-center text-sm tabular-nums text-gray-600">
                Page <span className="font-semibold text-gray-900">{page}</span>
                <span className="text-gray-400"> / </span>
                <span className="font-semibold text-gray-900">{totalPages}</span>
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-gray-800 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-400 disabled:shadow-none"
              >
                Next
                <ChevronRightIcon className="h-4 w-4 shrink-0" aria-hidden />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bill Modal */}
      {isBillModalOpen && selectedBillOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={() => setIsBillModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">Order Bill</h3>
                <button
                  onClick={() => setIsBillModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close bill modal"
                >
                  ×
                </button>
              </div>

              <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-gray-50/80 p-4 sm:flex-row sm:items-end sm:justify-between">
                <div className="w-full sm:max-w-xs">
                  <label
                    htmlFor="order-bill-printer-format"
                    className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500"
                  >
                    Printer format
                  </label>
                  <select
                    id="order-bill-printer-format"
                    value={billPrintFormat}
                    onChange={(e) => handleBillPrintFormatChange(e.target.value as BillPrintFormat)}
                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500/30"
                  >
                    {BILL_PRINT_FORMAT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => handlePrintOrderBill(selectedBillOrder, billPrintFormat)}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-black focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 sm:w-auto"
                >
                  <PrinterIcon className="h-4 w-4 shrink-0" aria-hidden />
                  Print bill
                </button>
              </div>

              <div className="border rounded-lg p-4 bg-white space-y-3">
                <div className="text-center">
                  <h4 className="text-xl font-semibold text-gray-900">{getRestaurantDetails()?.name || 'Restaurant'}</h4>
                  {getRestaurantDetails()?.address && (
                    <p className="text-sm text-gray-600">{getRestaurantDetails()?.address}</p>
                  )}
                  {getRestaurantDetails()?.phone && (
                    <p className="text-sm text-gray-600">{getRestaurantDetails()?.phone}</p>
                  )}
                </div>

                <div className="border-t border-dashed pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                  <p><span className="text-gray-500">Invoice:</span> <span className="font-medium">{selectedBillOrder.invoiceNo || selectedBillOrder.referenceId || selectedBillOrder._id}</span></p>
                  <p><span className="text-gray-500">Date:</span> <span className="font-medium">{formatDate(selectedBillOrder.createdAt)}</span></p>
                  {selectedBillOrder.table && <p><span className="text-gray-500">Table:</span> <span className="font-medium">{selectedBillOrder.table}</span></p>}
                  {selectedBillOrder.createdBy && <p><span className="text-gray-500">Customer:</span> <span className="font-medium">{selectedBillOrder.createdBy}</span></p>}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="py-2 text-left font-semibold text-gray-700">Item</th>
                        <th className="py-2 text-right font-semibold text-gray-700">Qty</th>
                        <th className="py-2 text-right font-semibold text-gray-700">Rate</th>
                        <th className="py-2 text-right font-semibold text-gray-700">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedBillOrder.products.map((product, index) => (
                        <tr key={`${selectedBillOrder._id}-${index}`} className="border-b border-gray-100">
                          <td className="py-2 text-gray-900">{product.name}</td>
                          <td className="py-2 text-right text-gray-700">{product.quantity}</td>
                          <td className="py-2 text-right text-gray-700">{formatCurrency(product.offerPrice)}</td>
                          <td className="py-2 text-right text-gray-900">{formatCurrency(product.offerPrice * product.quantity)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="border-t pt-3 space-y-1 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal</span>
                    <span>{formatCurrency(getOrderSubtotal(selectedBillOrder))}</span>
                  </div>
                  <div className="flex justify-between text-base font-semibold text-gray-900">
                    <span>Total</span>
                    <span>{formatCurrency(selectedBillOrder.totalAmount)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Kitchen Items Modal */}
      {isKitchenItemsModalOpen && selectedKitchenOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={() => setIsKitchenItemsModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6 space-y-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Order Items</h3>
                  <p className="text-sm text-gray-500">
                    {selectedKitchenOrder.invoiceNo || selectedKitchenOrder.referenceId || selectedKitchenOrder._id}
                  </p>
                </div>
                <button
                  onClick={() => setIsKitchenItemsModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close order items"
                >
                  ×
                </button>
              </div>

              <div className="border rounded-lg p-4 bg-white">
                <div className="space-y-3">
                  {selectedKitchenOrder.products.map((product, idx) => {
                    const productAny = product as any;
                    const itemComment = productAny.comment || productAny.note || productAny.notes || '';
                    return (
                      <div key={`${selectedKitchenOrder._id}-${idx}`} className="border-b border-gray-100 pb-3 last:border-b-0">
                        <div className="flex justify-between items-start gap-3">
                          <p className="font-medium text-gray-900">{product.name}</p>
                          <p className="text-sm text-gray-600">Qty: {product.quantity}</p>
                        </div>
                        {itemComment && (
                          <p className="text-sm text-orange-700 mt-1">Item comment: {String(itemComment)}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {selectedKitchenOrder.comment && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="text-sm text-orange-800">
                    <span className="font-medium">Order comment:</span> {selectedKitchenOrder.comment}
                  </p>
                </div>
              )}

              {isKitchenStaff && isStatusUpdateAllowed(selectedKitchenOrder, 'Ready') && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleKitchenMarkReady}
                    disabled={isUpdating}
                    className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdating ? 'Saving...' : 'Mark Ready'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {isStatusModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={() => setIsStatusModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Confirm</h3>
                <button 
                  onClick={() => setIsStatusModalOpen(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Order: <span className="font-medium text-gray-900">{selectedOrder.referenceId}</span></p>
                  <p className="text-sm text-gray-600">Total: <span className="font-medium text-gray-900">₹{selectedOrder.totalAmount}</span></p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Order is</label>
                  <div className="grid grid-cols-2 gap-2">
                    {allowedStatusTargets
                      .filter((status) => isStatusUpdateAllowed(selectedOrder, status))
                      .map((status) => (
                      <button
                        key={status}
                        type="button"
                        onClick={() => setNewStatus(status)}
                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          newStatus === status
                            ? 'bg-orange-600 text-white ring-2 ring-orange-500'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border border-gray-300'
                        }`}
                      >
                        {status}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Comment (Optional)</label>
                <textarea
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  rows={3}
                  placeholder="Add any notes about this order update..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600 resize-vertical"
                />
              </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button 
                    onClick={() => setIsStatusModalOpen(false)} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (shouldRequirePaymentBeforeDelivery(selectedOrder, newStatus)) {
                      setIsStatusModalOpen(false);
                      setStatusComment('');
                      setError('');
                      openPaymentModal(selectedOrder);
                      return;
                    }
                    void handleStatusUpdate();
                  }}
                  disabled={
                    isUpdating ||
                    (!isStatusUpdateAllowed(selectedOrder, newStatus) &&
                      !shouldRequirePaymentBeforeDelivery(selectedOrder, newStatus))
                  }
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-pink-600 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating
                    ? 'Saving...'
                    : shouldRequirePaymentBeforeDelivery(selectedOrder, newStatus)
                      ? 'Mark as Paid'
                      : getStatusTransitionActionLabel(newStatus)}
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mark as Paid Modal */}
      {isPaymentModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={closePaymentModal} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Mark as Paid</h3>
                <button
                  onClick={closePaymentModal}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close payment modal"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">
                    Order: <span className="font-medium text-gray-900">{selectedOrder.invoiceNo || selectedOrder.referenceId}</span>
                  </p>
                  <p className="text-sm text-gray-600">
                    Amount: <span className="font-medium text-gray-900">₹{selectedOrder.totalAmount}</span>
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Method</label>
                  <div className="grid grid-cols-2 gap-2">
                    {(Object.keys(paymentMethodLabels) as PaymentOption[]).map((method) => (
                      <button
                        key={method}
                        type="button"
                        onClick={() => handlePaymentMethodSelectionChange(method)}
                        className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                          paymentMethodSelection === method
                            ? 'bg-green-600 text-white border-green-600 ring-2 ring-green-500'
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {paymentMethodLabels[method]}
                      </button>
                    ))}
                  </div>
                </div>

                {paymentMethodSelection === 'cash' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Given Amount</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={cashReceivedAmount}
                      onChange={(e) => setCashReceivedAmount(e.target.value)}
                      placeholder="Enter amount received in cash"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <div className="mt-2 bg-green-50 border border-green-100 rounded-md p-2 text-sm">
                      <div className="flex justify-between text-gray-700">
                        <span>Bill Amount</span>
                        <span className="font-medium">₹{selectedOrder.totalAmount}</span>
                      </div>
                      <div className="flex justify-between text-gray-700 mt-1">
                        <span>Return Amount</span>
                        <span className="font-semibold text-green-700">₹{calculatedChangeAmount.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* {(paymentMethodSelection === 'card' || paymentMethodSelection === 'upi') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Reference ID (Optional)</label>
                    <input
                      type="text"
                      value={paymentReferenceId}
                      onChange={(e) => setPaymentReferenceId(e.target.value)}
                      placeholder="Txn/UTR/Auth reference"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                  </div>
                )} */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Note (Optional)</label>
                  <textarea
                    value={paymentNote}
                    onChange={(e) => setPaymentNote(e.target.value)}
                    rows={3}
                    placeholder="Add any payment remarks"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 resize-vertical"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button
                    onClick={closePaymentModal}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleMarkAsPaid}
                    disabled={isUpdating}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdating ? 'Saving...' : 'Confirm Payment'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Order Modal */}
      {isCancelModalOpen && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={() => setIsCancelModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Cancel Order</h3>
                <button 
                  onClick={() => setIsCancelModalOpen(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 mb-1">Order: <span className="font-medium text-gray-900">{selectedOrder.referenceId}</span></p>
                  <p className="text-sm text-gray-600">Total: <span className="font-medium text-gray-900">₹{selectedOrder.totalAmount}</span></p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Cancellation *</label>
                <textarea
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  rows={4}
                  placeholder="Please provide a reason for cancelling this order..."
                  required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-600 resize-vertical"
                />
              </div>

                <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
                  <p className="text-sm text-red-600">
                  <strong>Warning:</strong> This action will cancel the order and notify the customer.
                </p>
              </div>

                <div className="flex flex-col sm:flex-row gap-3 sm:justify-end">
                  <button 
                    onClick={() => setIsCancelModalOpen(false)} 
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                  >
                  Cancel
                </button>
                <button
                  onClick={handleCancelOrder}
                  disabled={isUpdating || !statusComment.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isUpdating ? 'Cancelling...' : 'Cancel Order'}
                </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {isNewOrderModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
          <div
            className="absolute inset-0 bg-slate-900/50 backdrop-blur-[2px]"
            onClick={() => setIsNewOrderModalOpen(false)}
            aria-hidden
          />
          <div
            className="relative w-full max-w-lg max-h-[min(90vh,640px)] flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/80"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-order-modal-title"
          >
            <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-4 sm:px-6">
              <h3 id="new-order-modal-title" className="text-lg font-semibold tracking-tight text-slate-900">
                New order
              </h3>
              <button
                type="button"
                onClick={() => setIsNewOrderModalOpen(false)}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
              <div className="space-y-0">
                <div>
                  <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Order type</p>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {NEW_ORDER_TYPE_OPTIONS.map(({ value, label }) => {
                      const selected = newOrderChannel === value;
                      return (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setNewOrderChannel(value)}
                          className={`rounded-xl border-2 px-3 py-3 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 ${
                            selected
                              ? 'border-orange-500 bg-orange-50 text-orange-950 shadow-sm'
                              : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {newOrderChannel === 'table' && (
                  <div>
                    <p className="mb-3 mt-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Table</p>
                    {tablesLoading ? (
                      <div className="flex items-center justify-center rounded-xl border border-dashed border-slate-200 py-12">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-orange-500 border-t-transparent" />
                      </div>
                    ) : tables.length === 0 ? (
                      <div className="rounded-xl border border-dashed border-slate-200 bg-slate-50/80 px-4 py-8 text-center text-sm text-slate-600">
                        No tables configured. Add tables in your setup to use this option.
                      </div>
                    ) : (
                      <div className="grid max-h-[220px] grid-cols-2 gap-2 overflow-y-auto pr-1 sm:grid-cols-3">
                        {tables.map((table) => {
                          const selected = newOrderTableId === table._id;
                          return (
                            <button
                              key={table._id}
                              type="button"
                              onClick={() => setNewOrderTableId(table._id)}
                              className={`rounded-xl border-2 px-3 py-3 text-center text-sm font-medium transition focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-1 ${
                                selected
                                  ? 'border-orange-500 bg-orange-50 text-orange-950 shadow-sm ring-1 ring-orange-200'
                                  : 'border-slate-200 bg-white text-slate-800 hover:border-slate-300 hover:bg-slate-50'
                              }`}
                            >
                              <span className="line-clamp-2">{table.name}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-slate-100 bg-slate-50/90 px-5 py-4 sm:flex-row sm:justify-end sm:px-6">
              <button
                type="button"
                onClick={() => setIsNewOrderModalOpen(false)}
                className="w-full rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2 sm:w-auto"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleOpenPublicMenuForNewOrder}
                className="w-full rounded-xl bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-orange-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2 sm:w-auto"
              >
                Next
              </button>
            </div>
          </div>
        </div>
      )}

      {isNewOrderPreviewModalOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-3 sm:p-5">
          <div
            className="absolute inset-0 bg-slate-900/60 backdrop-blur-[2px]"
            onClick={() => setIsNewOrderPreviewModalOpen(false)}
            aria-hidden
          />
          <div className="relative flex h-[92vh] w-full max-w-6xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-slate-200/80">
            <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3 sm:px-5">
              <h3 className="text-base font-semibold text-slate-900 sm:text-lg">Menu</h3>
              <button
                type="button"
                onClick={() => setIsNewOrderPreviewModalOpen(false)}
                className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2"
                aria-label="Close preview"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            {newOrderPreviewUrl ? (
              <iframe
                src={newOrderPreviewUrl}
                title="Customer menu"
                className="h-full w-full border-0"
                loading="lazy"
              />
            ) : (
              <div className="flex h-full items-center justify-center text-sm text-slate-500">
                Unable to load menu preview.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Customer Orders Modal */}
      {isCustomerModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={() => setIsCustomerModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Unpaid Bill Items</h3>
                  <p className="text-sm text-gray-500">{selectedCustomer}</p>
                </div>
                <button
                  onClick={() => setIsCustomerModalOpen(false)}
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                  aria-label="Close customer bill modal"
                >
                  ×
                </button>
              </div>

              <div className="mb-5 rounded-xl border border-gray-200 bg-gray-50/80 p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                  <div className="w-full lg:max-w-sm">
                    <label
                      htmlFor="customer-bill-printer-format"
                      className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500"
                    >
                      Printer format
                    </label>
                    <select
                      id="customer-bill-printer-format"
                      value={billPrintFormat}
                      onChange={(e) => handleBillPrintFormatChange(e.target.value as BillPrintFormat)}
                      className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 shadow-sm focus:border-pink-500 focus:outline-none focus:ring-2 focus:ring-pink-500/30"
                    >
                      {BILL_PRINT_FORMAT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                    <button
                      type="button"
                      onClick={handlePrint}
                      disabled={customerOrdersLoading || customerOrders.length === 0}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2.5 text-sm font-semibold text-gray-900 shadow-sm transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                    >
                      <PrinterIcon className="h-4 w-4 shrink-0" aria-hidden />
                      Print bill
                    </button>
                    <button
                      type="button"
                      onClick={handleSendPaymentLink}
                      disabled={customerOrdersLoading || customerOrders.length === 0 || isSendingPaymentLink}
                      title="Only works if payments are enabled in Settings."
                      className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                    >
                      {isSendingPaymentLink ? 'Sending…' : 'Send payment link'}
                    </button>
                  </div>
                </div>
              </div>
              
              <div id="customer-orders-print">
                {customerOrdersLoading ? (
                  <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                  </div>
                ) : customerOrders.length === 0 ? (
                  <div className="text-center py-8">
                    <p className="text-gray-500">No unpaid orders found for this customer.</p>
                  </div>
                ) : (
                  <div className="border rounded-lg p-4 bg-white space-y-3">
                    <div className="text-center">
                      <h4 className="text-xl font-semibold text-gray-900">{getRestaurantDetails()?.name || 'Restaurant'}</h4>
                      {getRestaurantDetails()?.address && (
                        <p className="text-sm text-gray-600">{getRestaurantDetails()?.address}</p>
                      )}
                      {getRestaurantDetails()?.phone && (
                        <p className="text-sm text-gray-600">{getRestaurantDetails()?.phone}</p>
                      )}
                    </div>
                    <div className="border-t border-dashed pt-3 text-sm">
                      <p><span className="text-gray-500">Customer:</span> <span className="font-medium">{selectedCustomer || '-'}</span></p>
                      <p><span className="text-gray-500">Generated:</span> <span className="font-medium">{new Date().toLocaleString('en-IN')}</span></p>
                    </div>

                    {customerOrders.map((order, index) => (
                      <div key={order._id} className="border rounded-lg p-4 bg-white space-y-3">
                        <div className="border-t border-dashed pt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
                          <p><span className="text-gray-500">Invoice:</span> <span className="font-medium">{order.invoiceNo || order.referenceId || order._id}</span></p>
                          <p><span className="text-gray-500">Date:</span> <span className="font-medium">{formatDate(order.createdAt)}</span></p>
                          {order.table && <p><span className="text-gray-500">Table:</span> <span className="font-medium">{order.table}</span></p>}
                          <p><span className="text-gray-500">Order #:</span> <span className="font-medium">{index + 1}</span></p>
                        </div>

                        <div className="overflow-x-auto">
                          <table className="min-w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="py-2 text-left font-semibold text-gray-700">Item</th>
                                <th className="py-2 text-right font-semibold text-gray-700">Qty</th>
                                <th className="py-2 text-right font-semibold text-gray-700">Rate</th>
                                <th className="py-2 text-right font-semibold text-gray-700">Amount</th>
                              </tr>
                            </thead>
                            <tbody>
                              {order.products.map((product, productIndex) => (
                                <tr key={productIndex} className="border-b border-gray-100">
                                  <td className="py-2 text-gray-900">{product.name}</td>
                                  <td className="py-2 text-right text-gray-700">{product.quantity}</td>
                                  <td className="py-2 text-right text-gray-700">{formatCurrency(product.offerPrice)}</td>
                                  <td className="py-2 text-right text-gray-900">{formatCurrency(product.offerPrice * product.quantity)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        <div className="border-t pt-3 space-y-1 text-sm">
                          <div className="flex justify-between text-gray-600">
                            <span>Subtotal</span>
                            <span>{formatCurrency(getOrderSubtotal(order))}</span>
                          </div>
                          <div className="flex justify-between text-base font-semibold text-gray-900">
                            <span>Total</span>
                            <span>{formatCurrency(order.totalAmount)}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    <div className="border-t pt-3 space-y-1 text-sm">
                      <div className="flex justify-between text-gray-600">
                        <span>Total Unpaid Orders</span>
                        <span>{customerOrders.length}</span>
                      </div>
                      <div className="flex justify-between text-base font-semibold text-gray-900">
                        <span>Grand Total</span>
                        <span>{formatCurrency(calculateTotalAmount(customerOrders))}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Table Details Modal */}
      {isTableModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-2 sm:p-4">
          <div onClick={() => setIsTableModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <div className="flex-1 min-w-0">
                  <h3 className="text-lg sm:text-xl font-semibold text-gray-900 truncate">Table: {selectedTable}</h3>
                  <p className="text-sm text-gray-500 mt-1">Manage users and table status</p>
                </div>
                <button 
                  onClick={() => setIsTableModalOpen(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none ml-2 flex-shrink-0"
                >
                  ×
                </button>
              </div>
              
              {tableDetailsLoading ? (
                <div className="flex items-center justify-center h-32">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
                </div>
              ) : tableDetails ? (
                <div className="space-y-4">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Table Information</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="font-medium text-gray-600 mb-1 sm:mb-0 sm:mr-2">Table Name:</span>
                        <span className="text-gray-900 font-semibold">{tableDetails.name}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center">
                        <span className="font-medium text-gray-600 mb-1 sm:mb-0 sm:mr-2">Status:</span>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          tableDetails.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                        }`}>
                          {tableDetails.status === 1 ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:col-span-2">
                        <span className="font-medium text-gray-600 mb-1 sm:mb-0 sm:mr-2">Created:</span>
                        <span className="text-gray-900">{formatDate(parseInt(tableDetails.createdAt))}</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-gray-900 mb-3">Current Users ({tableDetails.sessions?.length || 0})</h4>
                    {tableDetails.sessions && tableDetails.sessions.length > 0 ? (
                      <div className="space-y-3">
                        {tableDetails.sessions.map((session: any) => (
                          <div key={session._id} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                  session.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                                }`}>
                                  {session.status === 'active' ? 'Active User' : 'Inactive'}
                                </span>
                                <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                                  <span className="text-sm text-gray-600">User ID: <span className="font-mono text-xs">{session._id}</span></span>
                                  <span className="text-xs text-gray-500">Joined: {formatDate(session.createdAt)}</span>
                                </div>
                              </div>
                              {session.status === 'active' && (
                                <button
                                  onClick={() => handleRemoveSession(session)}
                                  className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors text-sm font-medium"
                                >
                                  Remove User
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6">
                        <div className="text-gray-400 mb-2">
                          <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                        </div>
                        <p className="text-gray-500 text-sm">No users currently at this table</p>
                        <p className="text-gray-400 text-xs mt-1">Table is available for new customers</p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-gray-500">Table details not found.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Remove User Confirmation Modal */}
      {isRemoveSessionModalOpen && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div onClick={() => setIsRemoveSessionModalOpen(false)} className="absolute inset-0" />
          <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto relative">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-center mb-4 sm:mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Remove User & Clear Table</h3>
                <button 
                  onClick={() => setIsRemoveSessionModalOpen(false)} 
                  className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <div className="space-y-4">
                <div className="text-center">
                  <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                    <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Confirm User Removal</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Are you sure you want to remove this user and clear the table? This action will end their session and make the table available for new customers.
                  </p>
                </div>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h5 className="font-medium text-gray-900 mb-2">User Details:</h5>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">User ID:</span>
                      <span className="font-mono text-xs text-gray-900">{selectedSession._id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      <span className="font-medium text-gray-900 capitalize">{selectedSession.status}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Joined:</span>
                      <span className="text-gray-900">{formatDate(selectedSession.createdAt)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3">
                  <button
                    onClick={() => setIsRemoveSessionModalOpen(false)}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmRemoveSession}
                    disabled={isRemovingSession}
                    className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50 transition-colors"
                  >
                    {isRemovingSession ? 'Removing User...' : 'Remove User'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      
    </div>
  );
};

export default OrderList;